import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Link,
  Tabs, // Added Tabs
  Tab,  // Added Tab
  IconButton, // Added IconButton
  ListItemButton, // Added ListItemButton
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon, // Added DownloadIcon
  PlayArrow as PlayArrowIcon, // Added PlayArrowIcon
} from '@mui/icons-material';
import { sheetsService } from '../services/googleSheetsService';

// Constants for required Intro/Outro fields
const REQUIRED_INTRO_OUTRO_FIELDS: { [key: string]: string } = {
  'carousel_video1_url': 'Carousel Video 1 URL',
  'carousel_video1_seek': 'Carousel Video 1 Seek',
  'carousel_video2_url': 'Carousel Video 2 URL',
  'carousel_video2_seek': 'Carousel Video 2 Seek',
  'outro_video1_url': 'Outro Video 1 URL',
  'outro_video1_seek': 'Outro Video 1 Seek',
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const DISPLAY_ORDER = ['Intro', '10', '9', '8', '7', '6', '5', '4', '3', '2', '1', 'Outro'];

// --- ADD THIS CONSTANT ---
const MASTER_VIDEO_SHEET_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo';

interface OutputManagerProps {
  spreadsheetId: string; // This is the Render Sheet ID passed as a prop
}

interface ClipInstruction {
  position: number;
  s3Url: string;
  artist: string;
  song: string;
  voIn: string;
  voOut: string;
  inPoint: string;
  duration: string;
}

// Add this interface at the top of the file, near other interfaces
interface SocialClipJobStatus {
  status: string;
  progress?: number;
  error?: string | null; // Can be string or null
  results?: Array<{
    position: number;
    verticalUrl: string;
    success: boolean;
    // Optional detailed fields from server
    status?: string;
    artist?: string;
    song?: string;
    s3PrePath?: string;
    sieveJobId?: string;
    sieveStatus?: string;
    s3PostPath?: string;
    error?: string; // Error specific to a single result item
  }>;
  weeklySheetId?: string;
}

export const OutputManager: React.FC<OutputManagerProps> = ({ spreadsheetId: propSpreadsheetId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  
  // Core State Declarations
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [outputFormat, setOutputFormat] = useState<string[]>([]);
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(propSpreadsheetId); // For Render Sheet ID context
  const [parentSpreadsheetId, setParentSpreadsheetId] = useState<string | null>(null); // For Parent/Data Sheet ID context
  
  // VO state variables
  const [voInPoints, setVoInPoints] = useState<{ [key: string]: string }>({});
  const [voOutPoints, setVoOutPoints] = useState<{ [key: string]: string }>({});
  const [songTitles, setSongTitles] = useState<{ [key: string]: string }>({});
  const [songArtists, setSongArtists] = useState<{ [key: string]: string }>({});
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null);
  const [lineAudioFiles, setLineAudioFiles] = useState<{ [key: string]: string }>({});
  const [renderSheetId, setRenderSheetId] = useState<string | null>(null);
  const [sheetDate, setSheetDate] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  
  // Social clip column indices/data
  const [s3UrlColIndex, setS3UrlColIndex] = useState<number | null>(null);
  const [inPointColIndex, setInPointColIndex] = useState<number | null>(null);
  const [durationColIndex, setDurationColIndex] = useState<number | null>(null);
  const [positionColIndex, setPositionColIndex] = useState<number | null>(null);
  const [s3Urls, setS3Urls] = useState<{ [key: string]: string }>({});
  const [videoInPoints, setVideoInPoints] = useState<{ [key: string]: string }>({});
  const [videoDurations, setVideoDurations] = useState<{ [key: string]: string }>({});
  const [chartPositions, setChartPositions] = useState<{ [key: string]: string }>({});
  
  // Polling-related state variables
  const [socialClipJobId, setSocialClipJobId] = useState<string | null>(null);
  const [socialClipJobStatus, setSocialClipJobStatus] = useState<string | null>(null);
  const [socialClipJobProgress, setSocialClipJobProgress] = useState<number | null>(null);
  const [socialClipErrors, setSocialClipErrors] = useState<string[]>([]);
  const [isPollingSocialClips, setIsPollingSocialClips] = useState<boolean>(false);
  
  // State for final video assembly
  const [isAssemblingVideo, setIsAssemblingVideo] = useState<boolean>(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [assemblyError, setAssemblyError] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [assemblySuccessMessage, setAssemblySuccessMessage] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  
  // State for movie rendering jobs
  const [movieProjects, setMovieProjects] = useState<{id: string, format: string, status: string, url?: string}[]>([]);
  const [isPollingMovieStatus, setIsPollingMovieStatus] = useState<boolean>(false);
  const [activePlayerTab, setActivePlayerTab] = useState(0);
  const [recentOutputs, setRecentOutputs] = useState<any[]>([]); // Added for recent outputs list
  const [baseFileNameForExport, setBaseFileNameForExport] = useState<string>(''); // Added for base filename
  const [selectedRecentVideoUrl, setSelectedRecentVideoUrl] = useState<string | null>(null); // Added for playing recent render
  const [renderProgress, setRenderProgress] = useState<number>(0);
  const [renderMessage, setRenderMessage] = useState<string>('');
  const [isReadyForExport, setIsReadyForExport] = useState<boolean>(false); // Ensure this is added
  
  const isPollingSocialClipsRef = useRef(isPollingSocialClips);
  useEffect(() => {
    isPollingSocialClipsRef.current = isPollingSocialClips;
  }, [isPollingSocialClips]);

  const isPollingMovieStatusRef = useRef(isPollingMovieStatus);
  useEffect(() => {
    isPollingMovieStatusRef.current = isPollingMovieStatus;
  }, [isPollingMovieStatus]);
  
  const movieProjectsRef = useRef(movieProjects);
  useEffect(() => {
    movieProjectsRef.current = movieProjects;
  }, [movieProjects]);

  const pollingInitiatedForJobRef = useRef<string | null>(null);
  
  // useEffect to load lineAudioFiles (data URLs for VOs) from localStorage using PARENT_SPREADSHEET_ID
  useEffect(() => {
    if (parentSpreadsheetId) {
      const savedLineAudioFiles = localStorage.getItem(`lineAudioFiles_${parentSpreadsheetId}`);
      if (savedLineAudioFiles) {
        try {
          const parsed = JSON.parse(savedLineAudioFiles);
          setLineAudioFiles(parsed);
          console.log(`[OutputManager] Loaded lineAudioFiles from localStorage for PARENT sheet ${parentSpreadsheetId}:`, parsed);
        } catch (e) {
          console.error(`[OutputManager] Failed to parse lineAudioFiles from localStorage for PARENT sheet ${parentSpreadsheetId}:`, e);
          setLineAudioFiles({});
        }
      } else {
        console.log(`[OutputManager] No lineAudioFiles found in localStorage for PARENT sheet ${parentSpreadsheetId}.`);
        setLineAudioFiles({});
      }
    } else {
      setLineAudioFiles({}); 
    }
  }, [parentSpreadsheetId]);
  
  useEffect(() => {
    if (propSpreadsheetId) {
      const savedLineAudioFiles = localStorage.getItem(`lineAudioFiles_${propSpreadsheetId}`);
      if (savedLineAudioFiles) {
        try {
          const parsed = JSON.parse(savedLineAudioFiles);
          setLineAudioFiles(parsed);
          console.log(`[OutputManager] Loaded lineAudioFiles from localStorage for ${propSpreadsheetId}:`, parsed);
        } catch (e) {
          console.error(`[OutputManager] Failed to parse lineAudioFiles from localStorage for ${propSpreadsheetId}:`, e);
          setLineAudioFiles({});
        }
      } else {
        console.log(`[OutputManager] No lineAudioFiles found in localStorage for ${propSpreadsheetId}.`);
        setLineAudioFiles({});
      }
    } else {
      setLineAudioFiles({}); // Clear if no spreadsheetId
    }
  }, [propSpreadsheetId]);
  
  // Function to trigger the final video assembly API call
  const triggerFinalVideoAssembly = useCallback(async () => {
    if (!renderSheetId || !sheetDate) {
      setError('Render Sheet ID or Sheet Date is missing, cannot assemble video.');
      return;
    }

    setIsAssemblingVideo(true);
    setAssemblyError(null);
    setAssemblySuccessMessage(null);
    console.log('[OutputManager] triggerFinalVideoAssembly called');
    console.log(`[OutputManager Assembly] Current outputFormat state: ${JSON.stringify(outputFormat)}`);
    console.log(`[OutputManager Assembly] outputFormat includes horizontal: ${outputFormat.includes('horizontal')}`);
    console.log(`[OutputManager Assembly] outputFormat includes vertical: ${outputFormat.includes('vertical')}`);

    try {
      // 1. Fetch all necessary data from the Render Sheet
      console.log(`[OutputManager Assembly] Fetching headers from render sheet: ${renderSheetId}`);
      const headers = await sheetsService.getHeaders(renderSheetId, 'Sheet1');
      
      console.log(`[OutputManager Assembly] Fetching chart data from render sheet: ${renderSheetId}`);
      // Fetching a bit more (A1:Z20) to ensure all chart data and some settings are covered.
      // Actual rows for ranking: 2-11 (for positions 10-1)
      // Actual rows for intro/outro specific items in main table: 12-13
      const chartDataResponse = await sheetsService.getData(renderSheetId, 'Sheet1!A1:Z20');
      const allRows = chartDataResponse.values || [];
      
      if (allRows.length < 2) { // Need at least headers + 1 data row
        throw new Error('Not enough data rows found in Render Sheet for assembly.');
      }

      const dataRows = allRows.slice(1); // Exclude header row

      console.log(`[OutputManager Assembly] Fetching settings data (A15:B70) from render sheet: ${renderSheetId}`);
      const settingsDataResponse = await sheetsService.getData(renderSheetId, 'Sheet1!A15:B70');
      const settingsRows = settingsDataResponse.values || [];
      const settingsMap = new Map<string, string>();
      settingsRows.forEach(row => {
        if (row && row.length >= 2 && row[0] && typeof row[0] === 'string' && typeof row[1] === 'string') {
          settingsMap.set(row[0].trim(), row[1].trim());
        }
      });
      console.log('[OutputManager Assembly] Parsed settingsMap:', Object.fromEntries(settingsMap));

      // 2. Identify column indices from headers
      const getIndex = (colName: string, required = true) => {
        const index = headers.findIndex(h => h?.toLowerCase() === colName.toLowerCase());
        if (index === -1 && required) {
          throw new Error(`Required column "${colName}" not found in Render Sheet headers.`);
        }
        return index;
      };

      const posIdx = getIndex('Position');
      const s3UrlIdx = getIndex('S3 URL'); 
      const inPointIdx = getIndex('IN Point');
      const durationIdx = getIndex('Duration');
      const zoomIdx = getIndex('Zoom');
      const songIdx = getIndex('Song');
      const artistIdx = getIndex('Artist');
      const lastWeekIdx = getIndex('Last Week');
      const peakIdx = getIndex('Peak');
      const weeksIdx = getIndex('Weeks');
      const verticalUrlColIdx = getIndex('Vertical URL', false); // Add this, false as it might not be there if social clips didn't run
      const messageColIdx = getIndex('Message', false); // ADD THIS LINE to get the message column index
      const extraTimeIdx = getIndex('Extra time', false); // Add Extra time column index

      // 3. Construct the 'ranking' array
      const rankingArray: any[] = [];
      let formattedSheetDate = 'unknown-date';
      if (sheetDate) {
        try {
          const dateObj = new Date(sheetDate);
          if (!isNaN(dateObj.getTime())) {
            const month = dateObj.getMonth() + 1;
            const day = dateObj.getDate();
            const year = dateObj.getFullYear().toString().slice(-2); // YY format
            formattedSheetDate = `${month}-${day}-${year}`;
          } 
        } catch (e) { console.error('Error formatting sheetDate for VO URLs:', e); }
      }

      // Iterate through chart data rows (typically rows 2-11 in sheet are for positions 10-1)
      // The sheet rows are 0-indexed in dataRows array (after slicing headers)
      // Row 0 of dataRows = Sheet Row 2 (Position 10)
      // Row 9 of dataRows = Sheet Row 11 (Position 1)
      for (let i = 0; i < 10; i++) { // For positions 10 down to 1
        const row = dataRows[i];
        if (!row) continue; 

        const position = parseInt(row[posIdx], 10);
        if (isNaN(position) || position < 1 || position > 10) {
            console.warn(`[OutputManager Assembly] Skipping row ${i+2} due to invalid position: ${row[posIdx]}`);
            continue;
        }

        const songName = row[songIdx] || '';
        const artistName = row[artistIdx] || '';
        const songMessage = messageColIdx !== -1 && row[messageColIdx] ? String(row[messageColIdx]).trim() : ""; // ADD THIS LINE to get the message for the current song
        
        let actualVerticalUrl = '';
        
        // Check if there's actually a vertical URL in the sheet for this position
        const hasRealVerticalUrlInSheet = verticalUrlColIdx !== -1 && row[verticalUrlColIdx] && String(row[verticalUrlColIdx]).trim() !== '';
        
        if (hasRealVerticalUrlInSheet) {
            actualVerticalUrl = String(row[verticalUrlColIdx]).trim();
            console.log(`[OutputManager Assembly] For position ${position} (Song: ${songName}), using Vertical URL from sheet: ${actualVerticalUrl}`);
        } else {
            // Only use fallback URL if NOT vertical format (to avoid filling in predicted URLs when we need to detect missing ones)
            if (!outputFormat.includes('vertical')) {
                actualVerticalUrl = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/Videos/${encodeURIComponent(artistName)} - ${encodeURIComponent(songName)} VERTICAL.mp4`;
                console.log(`[OutputManager Assembly] For position ${position} (Song: ${songName}), using fallback URL (non-vertical format): ${actualVerticalUrl}`);
            } else {
                // For vertical format, we'll detect and generate these later - leave empty for now
                actualVerticalUrl = '';
                console.log(`[OutputManager Assembly] For position ${position} (Song: ${songName}), no vertical URL in sheet - will generate later`);
            }
        }

        // Determine voiceover URL
        const lineKeyForVO = String(position); // Keys for ranked songs are '10', '9', ..., '1'
        let voiceoverUrlToUse = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/VO/${formattedSheetDate}/${position}.wav`; // Default S3 URL

        if (lineAudioFiles && lineAudioFiles[lineKeyForVO]) {
          voiceoverUrlToUse = lineAudioFiles[lineKeyForVO];
          console.log(`[OutputManager Assembly] Using lineAudioFile from localStorage for position ${position} (key ${lineKeyForVO}).`);
        } else {
          console.log(`[OutputManager Assembly] No lineAudioFile in localStorage for position ${position} (key ${lineKeyForVO}). Using default S3 URL: ${voiceoverUrlToUse}`);
        }
        
        // Calculate actual VO duration for vertical clip seek calculation
        let actualVoDuration = 0;
        const voInStr = voInPoints[lineKeyForVO];
        const voOutStr = voOutPoints[lineKeyForVO];
        if (voInStr && voOutStr) {
          const voInSeconds = parseFloat(voInStr);
          const voOutSeconds = parseFloat(voOutStr);
          if (!isNaN(voInSeconds) && !isNaN(voOutSeconds) && voOutSeconds > voInSeconds) {
            actualVoDuration = voOutSeconds - voInSeconds;
          }
        }
        
        // Calculate vertical clip seek point
        // Work backwards from the end: clip_end - video_duration - VO_duration = VO_start_time
        // The vertical clip was created with: clip_duration = (inPoint + videoDuration) - max(0, inPoint - 10)
        // So we need to calculate the total clip duration and work backwards
        const videoInPoint = parseFloat(row[inPointIdx] || '0');
        const videoDuration = parseFloat(row[durationIdx] || '0');
        const assumedVoDuration = 10; // This matches the backend assumption
        
        let totalClipDuration;
        
        // Special handling for position 5 - use actual clip duration of 12 seconds
        if (position === 5) {
          totalClipDuration = 12; // Actual measured clip duration for song 5
          console.log(`[OutputManager Assembly] Position ${position}: Using actual clip duration of 12 seconds for song 5`);
        } else {
          // Calculate the clip start time (same logic as backend)
          const clipStartTime = Math.max(0, videoInPoint - assumedVoDuration);
          const clipEndTime = videoInPoint + videoDuration;
          totalClipDuration = clipEndTime - clipStartTime;
        }
        
        // Work backwards: VO should start at (clip_end - video_duration - VO_duration)
        // In terms of seek time within the clip: total_clip_duration - video_duration - VO_duration
        if (actualVoDuration > 0) {
          console.log(`[OutputManager Assembly] Position ${position}: Total clip duration ${totalClipDuration}s, video duration ${videoDuration}s, VO duration ${actualVoDuration}s`);
        } else {
          console.log(`[OutputManager Assembly] Position ${position}: No valid VO duration found.`);
        }
        
        const rawLastWeek = row[lastWeekIdx];
        // JSON2Video template expressions expect numeric values
        // Convert empty cells, "-", or invalid values to 0 (meaning "no previous position")
        // Keep valid numbers as numbers
        let finalLastWeek: number;
        if (!rawLastWeek || rawLastWeek.trim() === '' || rawLastWeek === '-') {
          finalLastWeek = 0;
        } else {
          const parsedLastWeek = parseInt(rawLastWeek, 10);
          finalLastWeek = isNaN(parsedLastWeek) ? 0 : parsedLastWeek;
        }
        
        // Get extra time value for this position
        const rawExtraTime = extraTimeIdx !== -1 ? row[extraTimeIdx] : '';
        const extraTime = rawExtraTime ? parseFloat(rawExtraTime) : 0;
        
        // Generate the correct S3 URL based on artist and song name instead of trusting sheet data
        const correctS3Url = `https://c1-scott-scratchdisk.s3.us-west-2.amazonaws.com/Billboard/Videos/${encodeURIComponent(artistName)} - ${encodeURIComponent(songName)}.mp4`;
        console.log(`[OutputManager Assembly] Position ${position}: Using generated S3 URL: ${correctS3Url} instead of sheet URL: ${row[s3UrlIdx] || 'N/A'}`);
        
        rankingArray.push({
          song: songName,
          artist: artistName,
          position: position, // Keep original position if needed elsewhere by template
          this_week: position, // Explicitly add this_week
          last_week: finalLastWeek,
          peak_position: parseInt(row[peakIdx] || '0', 10),
          weeks_on_chart: parseInt(row[weeksIdx] || '0', 10),
          videoclip_url: correctS3Url, // Use generated URL instead of sheet data
          videoclip_seek: parseFloat(row[inPointIdx] || '0'),
          videoclip_duration: parseFloat(row[durationIdx] || '0'),
          videoclip_zoom: String(row[zoomIdx]).toLowerCase() === 'true',
          message: songMessage, // UPDATE THIS LINE to use the fetched songMessage
          voiceover_url: voiceoverUrlToUse,
          vertical_videoclip_url: actualVerticalUrl, // MODIFIED TO USE actualVerticalUrl
          horizontal_extra_time: extraTime // Add horizontal_extra_time for horizontal template
        });
      }
      // Ensure ranking is sorted by position 10 down to 1 as per example
      console.log('[OutputManager Assembly] Constructed ranking array:', JSON.stringify(rankingArray.slice(0,2))); // Log first 2 for brevity

      // 3.5. Check if vertical format is selected and create missing vertical videos
      if (outputFormat.includes('vertical')) {
        console.log('[OutputManager Assembly] Vertical format selected. Checking for missing vertical videos...');
        console.log(`[OutputManager Assembly Debug] verticalUrlColIdx: ${verticalUrlColIdx}`);
        console.log(`[OutputManager Assembly Debug] dataRows length: ${dataRows.length}`);
        
        // Find entries that don't have existing vertical URLs in the sheet
        const missingVerticalEntries = [];
        for (const entry of rankingArray) {
          // Check if this entry has an empty vertical_videoclip_url (means no URL in sheet)
          const hasVerticalUrl = entry.vertical_videoclip_url && entry.vertical_videoclip_url.trim() !== '';
          const expectedVerticalUrl = `https://c1-scott-scratchdisk.s3.us-west-2.amazonaws.com/Billboard/Videos/${entry.artist} - ${entry.song} VERTICAL.mp4`;
          
          console.log(`[OutputManager Assembly Debug] Position ${entry.position}: vertical_videoclip_url="${entry.vertical_videoclip_url}", hasVerticalUrl=${hasVerticalUrl}`);
          
          if (!hasVerticalUrl) {
            // No vertical URL exists - need to create it
            console.log(`[OutputManager Assembly Debug] Position ${entry.position}: NEEDS CREATION - ${entry.artist} - ${entry.song}`);
            missingVerticalEntries.push({
              position: entry.position,
              artist: entry.artist,
              song: entry.song,
              horizontalUrl: entry.videoclip_url,
              expectedVerticalUrl
            });
          } else {
            console.log(`[OutputManager Assembly Debug] Position ${entry.position}: HAS URL - ${entry.artist} - ${entry.song}`);
          }
        }
        
        if (missingVerticalEntries.length > 0) {
          console.log(`[OutputManager Assembly] Found ${missingVerticalEntries.length} missing vertical videos:`, 
            missingVerticalEntries.map(e => `${e.artist} - ${e.song} (pos ${e.position})`));
          
          // Create missing vertical videos using existing social clips workflow
          const clipInstructions = missingVerticalEntries.map(entry => ({
            position: entry.position,
            s3Url: entry.horizontalUrl,
            artist: entry.artist,
            song: entry.song,
            voIn: '0',
            voOut: '0', 
            inPoint: '0',
            duration: '0'
          }));
          
          console.log('[OutputManager Assembly] Starting direct Sieve processing for missing vertical videos...');
          
          // Start all Sieve jobs in parallel
          const verticalJobs = [];
          
          console.log(`[OutputManager Assembly] Starting ${missingVerticalEntries.length} Sieve jobs in parallel...`);
          
          // Start all jobs concurrently
          const jobPromises = missingVerticalEntries.map(async (entry) => {
            console.log(`[OutputManager Assembly] Starting vertical job for: ${entry.artist} - ${entry.song}`);
            
            try {
              const response = await fetch(`${API_BASE_URL}/api/generate-vertical-from-s3`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  s3Url: entry.horizontalUrl,
                  songName: entry.song,
                  artistName: entry.artist
                })
              });
              
              if (!response.ok) {
                throw new Error(`Failed to start vertical job for ${entry.artist} - ${entry.song}: ${response.status}`);
              }
              
              const result = await response.json();
              console.log(`[OutputManager Assembly] Started Sieve job for ${entry.artist} - ${entry.song}:`, result.sieveJobId);
              
              return {
                position: entry.position,
                artist: entry.artist,
                song: entry.song,
                sieveJobId: result.sieveJobId,
                expectedUrl: entry.expectedVerticalUrl // Use the expected URL we calculated
              };
              
            } catch (error) {
              console.error(`[OutputManager Assembly] Error starting vertical job for ${entry.artist} - ${entry.song}:`, error);
              throw error;
            }
          });
          
          // Wait for all jobs to start
          const allJobsStarted = await Promise.all(jobPromises);
          verticalJobs.push(...allJobsStarted);
          
          // Now poll all jobs in parallel until all complete
          if (verticalJobs.length > 0) {
            console.log(`[OutputManager Assembly] Polling ${verticalJobs.length} Sieve jobs in parallel...`);
            
            // Poll all jobs concurrently
            const pollPromises = verticalJobs.map(async (job) => {
              const maxAttempts = 60; // 10 minutes max per job
              let attempts = 0;
              
              while (attempts < maxAttempts) {
                attempts++;
                console.log(`[OutputManager Assembly] Polling ${job.artist} - ${job.song} (attempt ${attempts}/${maxAttempts})`);
                
                try {
                  const response = await fetch(`${API_BASE_URL}/api/check-sieve-job/${job.sieveJobId}?songName=${encodeURIComponent(job.song)}&artistName=${encodeURIComponent(job.artist)}`);
                  
                  if (!response.ok) {
                    console.warn(`[OutputManager Assembly] Failed to check job ${job.sieveJobId}: ${response.status}`);
                    await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10s before retry
                    continue;
                  }
                  
                  const result = await response.json();
                  
                  if (result.status === 'completed') {
                    console.log(`[OutputManager Assembly] ✅ ${job.artist} - ${job.song} completed successfully`);
                    return { ...job, s3Url: result.s3Url, success: true };
                  } else if (result.status === 'failed') {
                    console.error(`[OutputManager Assembly] ❌ ${job.artist} - ${job.song} failed: ${result.error}`);
                    return { ...job, success: false, error: result.error };
                  } else if (result.status === 'processing') {
                    console.log(`[OutputManager Assembly] ⏳ ${job.artist} - ${job.song} still processing...`);
                    await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10s before next poll
                    continue;
                  }
                  
                } catch (pollError) {
                  console.error(`[OutputManager Assembly] Error polling ${job.artist} - ${job.song}:`, pollError);
                  await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10s before retry
                }
              }
              
              // Timeout
              console.error(`[OutputManager Assembly] ⏰ ${job.artist} - ${job.song} timed out after ${maxAttempts} attempts`);
              return { ...job, success: false, error: 'Timeout' };
            });
            
            // Wait for all jobs to complete
            const allJobResults = await Promise.all(pollPromises);
            console.log(`[OutputManager Assembly] All ${verticalJobs.length} Sieve jobs completed`);
            
            // Check if any jobs failed
            const failedJobs = allJobResults.filter(job => !job.success);
            if (failedJobs.length > 0) {
              const failedNames = failedJobs.map(job => `${job.artist} - ${job.song}`).join(', ');
              throw new Error(`Failed to generate vertical videos for: ${failedNames}`);
            }
            
            // Update verticalJobs with completed results
            verticalJobs.splice(0, verticalJobs.length, ...allJobResults);
            
            // Update the ranking array with the expected vertical URLs
            for (const job of verticalJobs) {
              const rankingEntry = rankingArray.find(r => r.position === job.position);
              if (rankingEntry) {
                rankingEntry.vertical_videoclip_url = job.expectedUrl;
                console.log(`[OutputManager Assembly] Updated position ${job.position} with vertical URL: ${job.expectedUrl}`);
              }
            }
            
            console.log('[OutputManager Assembly] All vertical videos should now be available');
            
            // Now update both the master video list and render sheet with the new vertical URLs
            console.log('[OutputManager Assembly] Updating sheets with new vertical URLs...');
            try {
              // Prepare results for sheet update function
              const verticalResults = verticalJobs.map(job => ({
                position: job.position,
                verticalUrl: job.expectedUrl,
                success: true,
                song: job.song,
                artist: job.artist
              }));
              
              console.log(`[OutputManager Assembly] Calling updateSheetsWithVerticalUrls with ${verticalResults.length} results`);
              await updateSheetsWithVerticalUrls(MASTER_VIDEO_SHEET_ID, renderSheetId, verticalResults);
              console.log('[OutputManager Assembly] Successfully updated sheets with vertical URLs');
              enqueueSnackbar(`✅ Updated ${verticalResults.length} vertical URL(s) in both Master Video List and render sheet`, { variant: 'success' });
            } catch (sheetUpdateError) {
              console.error('[OutputManager Assembly] Error updating sheets with vertical URLs:', sheetUpdateError);
              enqueueSnackbar(`⚠️ Vertical videos generated but failed to update sheets: ${sheetUpdateError instanceof Error ? sheetUpdateError.message : 'Unknown error'}`, { variant: 'warning' });
            }
          }
          
        } else {
          console.log('[OutputManager Assembly] All vertical videos already exist in sheet');
        }
      }

      // 4. Construct the full variables payload
      const variables: { [key: string]: any } = {};
      variables.ranking = rankingArray;

      // Helper to get value from settingsMap, convert to number if key suggests it
      const getSetting = (key: string, defaultValue: any = null, isNumeric = false) => {
        const val = settingsMap.get(key);
        if (val === undefined || val === null || val.trim() === '') return defaultValue;
        return isNumeric ? parseFloat(val) : val;
      };
      
      // Format sheetDate to "Month Name D, YYYY"
      let displayDateString = 'Unknown Date';
      if (sheetDate) {
        try {
          const date = new Date(sheetDate);
          if (!isNaN(date.getTime())) {
            displayDateString = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
          }
        } catch (e) { console.error('Error formatting sheetDate for displayDateString', e);}
      }
      variables.date_string = displayDateString;

      // Populate other variables from settingsMap or defaults
      variables.intro_voiceover_url = getSetting('intro_voiceover_url') || `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/VO/${formattedSheetDate}/intro.wav`;
      variables.carousel_video1_url = getSetting('carousel_video1_url');
      variables.carousel_video1_seek = getSetting('carousel_video1_seek', '0', true);
      variables.carousel_video2_url = getSetting('carousel_video2_url');
      variables.carousel_video2_seek = getSetting('carousel_video2_seek', '0', true);
      variables.carousel_video3_url = getSetting('carousel_video3_url');
      variables.carousel_video3_seek = getSetting('carousel_video3_seek', '0', true);
      variables.carousel_video4_url = getSetting('carousel_video4_url');
      variables.carousel_video4_seek = getSetting('carousel_video4_seek', '0', true);
      
      variables.video_left_url = getSetting('video_left_url'); 

      variables.intro_volume_during_voiceover = getSetting('intro_volume_during_voiceover', '0.5', true);
      variables.intro_volume_after_voiceover = getSetting('intro_volume_after_voiceover', '0.75', true); // Corrected, expecting number
      variables.intro_voiceover_volume = getSetting('intro_voiceover_volume', '1.5', true);

      variables.outro_voiceover_url = getSetting('outro_voiceover_url') || `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/VO/${formattedSheetDate}/outro.wav`;
      variables.outro_video1_url = getSetting('outro_video1_url');
      variables.outro_video1_seek = getSetting('outro_video1_seek', '0', true);
      variables.outro_video2_url = getSetting('outro_video2_url');
      variables.outro_video2_seek = getSetting('outro_video2_seek', '0', true);
      variables.outro_video3_url = getSetting('outro_video3_url');
      variables.outro_video3_seek = getSetting('outro_video3_seek', '0', true);
      variables.outro_video4_url = getSetting('outro_video4_url');
      variables.outro_video4_seek = getSetting('outro_video4_seek', '0', true);
      variables.outro_video5_url = getSetting('outro_video5_url');
      variables.outro_video5_seek = getSetting('outro_video5_seek', '0', true);
      variables.outro_video6_url = getSetting('outro_video6_url');
      variables.outro_video6_seek = getSetting('outro_video6_seek', '0', true);

      variables.outro_volume_during_voiceover = getSetting('outro_volume_during_voiceover', '0.5', true);
      variables.outro_volume_after_voiceover = getSetting('outro_volume_after_voiceover', '0.75', true);
      variables.outro_voiceover_volume = getSetting('outro_voiceover_volume', '0.8', true);

      variables.videoclip_volume_during_voiceover = getSetting('videoclip_volume_during_voiceover', '0', true);
      variables.videoclip_volume_after_voiceover = getSetting('videoclip_volume_after_voiceover', '1.7', true);
      variables.videoclip_voiceover_volume = getSetting('videoclip_voiceover_volume', '1.5', true);
      variables.videoclip_volume_final_number1 = getSetting('videoclip_volume_final_number1', '1.7', true);
      variables.videoclip_volume_final_other = getSetting('videoclip_volume_final_other', '1.7', true);
      variables.in_point_adjust = getSetting('in_point_adjust', '-0.2', true);

      // Define which templates to use
      const horizontalTemplateId = "x2m9UXiXciWjkiPHpMxE";
      const verticalTemplateId = "nAEJWKr0acvVNydAfkdC";
      
      // Determine which templates to process based on the selected output format
      const templatesToProcess = [];
      
      if (outputFormat.includes('horizontal')) {
        templatesToProcess.push({ id: horizontalTemplateId, name: "Horizontal" });
      }
      
      if (outputFormat.includes('vertical')) {
        templatesToProcess.push({ id: verticalTemplateId, name: "Vertical" });
      }
      
      if (templatesToProcess.length === 0) {
        throw new Error('No output format selected. Please select Horizontal or Vertical format.');
      }
      
      console.log(`[OutputManager Assembly] Templates to process: ${JSON.stringify(templatesToProcess.map(t => ({ name: t.name, id: t.id })))}`);
      console.log(`[OutputManager Assembly] Will process ${templatesToProcess.length} template(s)`);
      
      // Process each template sequentially
      let successCount = 0;
      const newlySubmittedProjects: {id: string, format: string, status: string}[] = []; // Collect new projects here

      for (const template of templatesToProcess) {
        console.log(`[OutputManager Assembly] Processing template: ${template.name} (${template.id})`);
        
        // Create template-specific variables
        const templateVariables = { ...variables };
        
        // Template-specific processing can be added here if needed
        
        // Note: horizontal_extra_time is now included in both Horizontal and Vertical templates
        
        const finalPayload = {
          template: template.id,
          variables: templateVariables,
        };

        console.log(`[OutputManager Assembly] Final payload for json2video (${template.name}):`, JSON.stringify(finalPayload, null, 2));

        // 5. Make the API Call to json2video
        const response = await fetch('https://api.json2video.com/v2/movies', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': 'r6eXWIhUKTcDNNgqG2RdiSY8PtpFuEmDgb6fYYlu' // Hardcoded API key
          },
          body: JSON.stringify(finalPayload)
        });

        if (!response.ok) {
          let errorBody = `Unknown error during final ${template.name} video assembly.`;
          try {
            const errorJson = await response.json();
            errorBody = errorJson.message || errorJson.error || JSON.stringify(errorJson);
          } catch (parseError) {
            errorBody = await response.text();
          }
          // Don't throw error here; log it but continue to the next template if available
          console.error(`[OutputManager Assembly] Error in ${template.name} template assembly:`, errorBody);
          enqueueSnackbar(`${template.name} assembly failed: ${errorBody}`, { variant: 'error' });
          continue;
        }

        const assemblyResult = await response.json();
        console.log(`[OutputManager Assembly] json2video API Response for ${template.name}:`, assemblyResult);
        
        // Handle successful response with proper response format
        if (assemblyResult.success === true && assemblyResult.project) {
          const timestamp = assemblyResult.timestamp ? new Date(assemblyResult.timestamp).toLocaleString() : 'N/A';
          console.log(`[OutputManager Assembly] ${template.name} job submitted successfully - Project ID: ${assemblyResult.project}, Timestamp: ${timestamp}`);
          
          // Add to temporary array
          newlySubmittedProjects.push({
            id: assemblyResult.project,
            format: template.name,
            status: 'processing'
          });
          
          // Count successful template processing
          successCount++;
          enqueueSnackbar(`${template.name} assembly job started! Project ID: ${assemblyResult.project}. This may take 3-5 minutes to render.`, { variant: 'success' });
        } else {
          // Handle success response but unexpected format
          console.warn(`[OutputManager Assembly] ${template.name} job likely submitted, but response format unexpected:`, assemblyResult);
          successCount++;
          enqueueSnackbar(`${template.name} assembly initiated successfully!`, { variant: 'success' });
        }
      }
      
      // After the loop, update the state with all newly submitted projects
      if (newlySubmittedProjects.length > 0) {
        setMovieProjects(prevProjects => [
          ...prevProjects,
          ...newlySubmittedProjects
        ]);
        // Start polling if not already polling and there are new projects
        if (!isPollingMovieStatus) {
          setIsPollingMovieStatus(true);
        }
      }
      
      // Set final success message based on how many templates were processed successfully
      if (successCount === templatesToProcess.length) {
        const successMsg = `All ${successCount} video format(s) assembly initiated successfully.`;
        setAssemblySuccessMessage(successMsg);
      } else if (successCount > 0) {
        const successMsg = `${successCount} of ${templatesToProcess.length} video format(s) assembly initiated successfully.`;
        setAssemblySuccessMessage(successMsg);
      } else {
        throw new Error('All video format assemblies failed. Please check the logs for details.');
      }
      
    } catch (err) {
      console.error('[OutputManager] Error in triggerFinalVideoAssembly:', err);
      const message = err instanceof Error ? err.message : 'Failed to assemble final video';
      setAssemblyError(message);
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsAssemblingVideo(false);
    }
  }, [renderSheetId, sheetDate, outputFormat, enqueueSnackbar, setError, setIsAssemblingVideo, setAssemblyError, setAssemblySuccessMessage, isPollingMovieStatus, setMovieProjects, lineAudioFiles]); // sheetsService was already removed here in a previous commit, this is just to confirm
  
    // Define updateSheetsWithVerticalUrls as a component function
  // The parameters weeklySheetId and renderSheetIdArgument will now correctly receive
  // MASTER_VIDEO_SHEET_ID and the derived renderSheetId respectively.
  const updateSheetsWithVerticalUrls = useCallback(async (masterSheetIdForUpdate: string, renderSheetIdForUpdate: string, results: { position: number; verticalUrl: string; success: boolean; song?: string; artist?: string }[]) => {
    console.log(`[OutputManager] Attempting to update sheets with vertical URLs.`);
    console.log(`[OutputManager] === TARGET SHEET IDs BEING USED ===`);
    console.log(`[OutputManager] Master Video Sheet (received as masterSheetIdForUpdate): ${masterSheetIdForUpdate}`);
    console.log(`[OutputManager] Render Sheet (received as renderSheetIdForUpdate): ${renderSheetIdForUpdate}`);
    console.log(`[OutputManager] === END TARGET SHEET IDs ===`);
    console.log(`[OutputManager] Full vertical URL results to process: ${JSON.stringify(results.map(r => ({ position: r.position, url: r.verticalUrl })))}`);
    
    try {
      console.log(`[OutputManager] DEBUG - Component's propSpreadsheetId (Weekly from Charts): ${propSpreadsheetId}, Component's renderSheetId state (derived from Weekly): ${renderSheetId}`);
      console.log(`[OutputManager] DEBUG - Parameters received by function: masterSheetIdForUpdate=${masterSheetIdForUpdate}, renderSheetIdForUpdate=${renderSheetIdForUpdate}`);
      console.log(`[OutputManager] DEBUG - Is Master Video Sheet ID correct?: ${masterSheetIdForUpdate === MASTER_VIDEO_SHEET_ID}`);
      console.log(`[OutputManager] DEBUG - Is Render Sheet ID (arg) matching component state?: ${renderSheetId === renderSheetIdForUpdate}`);
      
      if (!masterSheetIdForUpdate || !renderSheetIdForUpdate) { 
        console.error('[OutputManager] ERROR - Missing one or both sheet IDs for update, cannot update sheets');
        throw new Error('Missing sheet IDs, cannot update sheets with vertical URLs');
      }
      
      const masterSheetHeaders = await sheetsService.getHeaders(masterSheetIdForUpdate, 'Sheet1');
      const renderSheetHeaders = await sheetsService.getHeaders(renderSheetIdForUpdate, 'Sheet1');
      
      console.log(`[OutputManager] Master Video Sheet headers: ${JSON.stringify(masterSheetHeaders)}`);
      console.log(`[OutputManager] Render sheet headers: ${JSON.stringify(renderSheetHeaders)}`);
      
      // Raw header inspection for exact matches (including case, whitespace, etc)
      console.log('[OutputManager] RAW HEADERS INSPECTION:');
      console.log('[OutputManager] Weekly Headers:');
      masterSheetHeaders.forEach((header, index) => {
        console.log(`[OutputManager] Weekly Header[${index}]: "${header}" (${typeof header})`);
        if (header === 'Vertical URL') {
          console.log(`[OutputManager] EXACT MATCH FOUND at index ${index}`);
        } else if (header && header.toLowerCase() === 'vertical url') {
          console.log(`[OutputManager] CASE INSENSITIVE MATCH FOUND at index ${index}`);
        } else if (header && header.trim() === 'Vertical URL') {
          console.log(`[OutputManager] TRIMMED MATCH FOUND at index ${index}`);
        } else if (header && header.includes('Vertical')) {
          console.log(`[OutputManager] PARTIAL MATCH (Vertical) FOUND at index ${index}`);
        } else if (header && header.includes('URL')) {
          console.log(`[OutputManager] PARTIAL MATCH (URL) FOUND at index ${index}`);
        }
      });
      
      console.log('[OutputManager] Render Headers:');
      renderSheetHeaders.forEach((header, index) => {
        console.log(`[OutputManager] Render Header[${index}]: "${header}" (${typeof header})`);
        if (header === 'Vertical URL') {
          console.log(`[OutputManager] EXACT MATCH FOUND at index ${index}`);
        } else if (header && header.toLowerCase() === 'vertical url') {
          console.log(`[OutputManager] CASE INSENSITIVE MATCH FOUND at index ${index}`);
        } else if (header && header.trim() === 'Vertical URL') {
          console.log(`[OutputManager] TRIMMED MATCH FOUND at index ${index}`);
        } else if (header && header.includes('Vertical')) {
          console.log(`[OutputManager] PARTIAL MATCH (Vertical) FOUND at index ${index}`);
        } else if (header && header.includes('URL')) {
          console.log(`[OutputManager] PARTIAL MATCH (URL) FOUND at index ${index}`);
        }
      });
      
      // Check for vertical URL column with multiple possible variations
      const possibleVerticalUrlNames = [
        'vertical url', 'verticalurl', 'vertical_url', 'vertical link', 'vertical video url',
        'vertical video', 'vertical', 'vertical mp4', 'vertical clip url'
      ];
      
      // --- NEW HELPER FUNCTION to find specific columns ---
      const findExactColumn = (headers: string[], targetNames: string[], sheetIdentifier: string): number => {
        for (const name of targetNames) {
          // Try exact match first
          let columnIndex = headers.findIndex(h => h === name);
          if (columnIndex !== -1) {
            console.log(`[OutputManager findExactColumn - ${sheetIdentifier}] Found exact match for "${name}" at index ${columnIndex}`);
            return columnIndex;
          }
          // Try case-insensitive match
          columnIndex = headers.findIndex(h => h && h.toLowerCase() === name.toLowerCase());
          if (columnIndex !== -1) {
            console.log(`[OutputManager findExactColumn - ${sheetIdentifier}] Found case-insensitive match for "${name}" at index ${columnIndex}`);
            return columnIndex;
          }
        }
        console.warn(`[OutputManager findExactColumn - ${sheetIdentifier}] FAILED to find any match for column using names: ${targetNames.join(', ')}.`);
        return -1;
      };

      // Find Vertical URL columns (using existing findColumnIndex or switch to findExactColumn)
      let masterVerticalUrlIndex = findExactColumn(masterSheetHeaders, possibleVerticalUrlNames, "Master"); // Or findExactColumn(masterSheetHeaders, ["Vertical URL"], "Master")
      let renderVerticalUrlIndex = findExactColumn(renderSheetHeaders, possibleVerticalUrlNames, "Render"); // Or findExactColumn(renderSheetHeaders, ["Vertical URL"], "Render")
      console.log(`[OutputManager] Master Vertical URL column index: ${masterVerticalUrlIndex}, Render Vertical URL column index: ${renderVerticalUrlIndex}`);

      // --- Find columns for Master Video Sheet ---
      const masterSongColIndex = findExactColumn(masterSheetHeaders, ["Song", "Song Title", "Title"], "Master");
      const masterArtistColIndex = findExactColumn(masterSheetHeaders, ["Artist", "Artist Name"], "Master");

      // --- Find columns for Render Sheet (Position is key here) ---
      const renderPositionColIndex = findExactColumn(renderSheetHeaders, ["Position", "#", "Pos"], "Render");

      console.log(`[OutputManager] Master Song column: ${masterSongColIndex}, Master Artist column: ${masterArtistColIndex}`);
      console.log(`[OutputManager] Render Position column: ${renderPositionColIndex}`);

      // Check if essential columns were found for master sheet updates
      if (masterVerticalUrlIndex === -1 || masterSongColIndex === -1 || masterArtistColIndex === -1) {
        console.error(`[OutputManager] Essential columns (Vertical URL, Song, or Artist) not found in Master Video Sheet. Cannot proceed with master sheet updates.`);
        // Decide if to throw error or just skip master updates
      }
      // Check if essential columns were found for render sheet updates
      if (renderVerticalUrlIndex === -1 || renderPositionColIndex === -1) {
        console.error(`[OutputManager] Essential columns (Vertical URL or Position) not found in Render Sheet. Cannot proceed with render sheet updates.`);
        // Decide if to throw error or just skip render updates
      }
      
      if ((masterVerticalUrlIndex === -1 || masterSongColIndex === -1 || masterArtistColIndex === -1) && 
          (renderVerticalUrlIndex === -1 || renderPositionColIndex === -1)) {
            throw new Error('Essential columns missing in both sheets. Please check column headers.');
      }

      // Get all rows to find the positions
      console.log(`[OutputManager] Fetching all rows from Master Video Spreadsheet ID: ${masterSheetIdForUpdate}`);
      const masterData = await sheetsService.getData(masterSheetIdForUpdate, 'Sheet1!A1:Z1000');
      console.log(`[OutputManager] Fetching all rows from Render spreadsheet ID: ${renderSheetIdForUpdate}`);
      const renderData = await sheetsService.getData(renderSheetIdForUpdate, 'Sheet1!A1:Z1000');
      
      if (!masterData.values || !renderData.values || masterData.values.length <= 1 || renderData.values.length <= 1) {
        console.error(`[OutputManager] No data found: Master rows: ${masterData.values?.length}, Render rows: ${renderData.values?.length}`);
        throw new Error('Could not find data rows in one or both sheets');
      }
      
      console.log(`[OutputManager] Master sheet rows: ${masterData.values.length}, Render sheet rows: ${renderData.values.length}`);
      
      const masterRows = masterData.values.slice(1); // Skip header row
      const renderRows = renderData.values.slice(1); // Skip header row
      
      // Helper to convert column index to letter
      const columnIndexToLetter = (index: number): string => {
        let letter = '';
        let tempIndex = index + 1; // Convert 0-based to 1-based for calculation
        while (tempIndex > 0) {
          const remainder = (tempIndex - 1) % 26;
          letter = String.fromCharCode(65 + remainder) + letter;
          tempIndex = Math.floor((tempIndex - 1) / 26);
        }
        return letter;
      };
      
      // Prepare batch updates for both sheets
      const masterUpdates: { range: string; values: string[][] }[] = [];
      const renderUpdates: { range: string; values: string[][] }[] = [];
      
      // For each successful result, find the matching row and prepare updates
      for (const result of results) {
        const { song, artist, verticalUrl, position } = result;
        const sL = String(song || '').trim().toLowerCase();
        const aL = String(artist || '').trim().toLowerCase();

        // --- Process Master Video Sheet (match by Song & Artist) ---
        if (masterVerticalUrlIndex !== -1 && masterSongColIndex !== -1 && masterArtistColIndex !== -1) {
          const masterRowIndex = masterRows.findIndex(row => {
            const rowSong = String(row[masterSongColIndex] || '').trim().toLowerCase();
            const rowArtist = String(row[masterArtistColIndex] || '').trim().toLowerCase();
            return rowSong === sL && rowArtist === aL;
          });

          if (masterRowIndex !== -1) {
            const sheetRowNumber = masterRowIndex + 2; // +1 for 0-based to 1-based, +1 for header row
            const columnLetter = columnIndexToLetter(masterVerticalUrlIndex);
            const range = `Sheet1!${columnLetter}${sheetRowNumber}`;
            masterUpdates.push({ range, values: [[verticalUrl]] });
            console.log(`[OutputManager] MASTER sheet: Will update Song "${song}", Artist "${artist}" at ${range} with URL: ${verticalUrl}`);
          } else {
            console.warn(`[OutputManager] MASTER sheet: Could not find Song "${song}", Artist "${artist}".`);
          }
        } else {
            console.warn('[OutputManager] MASTER sheet: Skipping updates due to missing Vertical URL, Song, or Artist column index.');
        }

        // --- Process Render Sheet (match by Position) ---
        if (renderVerticalUrlIndex !== -1 && renderPositionColIndex !== -1) {
          const posToMatch = String(position);
          const renderRowIndex = renderRows.findIndex(row => {
            const rowPosition = String(row[renderPositionColIndex] || '').trim();
            return rowPosition === posToMatch;
          });

          if (renderRowIndex !== -1) {
            const sheetRowNumber = renderRowIndex + 2;
            const columnLetter = columnIndexToLetter(renderVerticalUrlIndex);
            const range = `Sheet1!${columnLetter}${sheetRowNumber}`;
            renderUpdates.push({ range, values: [[verticalUrl]] });
            console.log(`[OutputManager] RENDER sheet: Will update Position ${posToMatch} at ${range} with URL: ${verticalUrl}`);
          } else {
            console.warn(`[OutputManager] RENDER sheet: Could not find Position ${posToMatch}.`);
          }
        } else {
            console.warn('[OutputManager] RENDER sheet: Skipping updates due to missing Vertical URL or Position column index.');
        }
      }
      
      // Execute batch updates if we have any
      if (masterUpdates.length > 0) {
        console.log(`[OutputManager] Executing batch update for MASTER sheet with ${masterUpdates.length} updates:`, JSON.stringify(masterUpdates));
        await sheetsService.batchUpdateValues(masterSheetIdForUpdate, masterUpdates);
        console.log(`[OutputManager] MASTER sheet updated successfully`);
        // Optional: Add verification for master sheet updates
      }

      if (renderUpdates.length > 0) {
        console.log(`[OutputManager] Executing batch update for RENDER sheet with ${renderUpdates.length} updates:`, JSON.stringify(renderUpdates));
        await sheetsService.batchUpdateValues(renderSheetIdForUpdate, renderUpdates);
        console.log(`[OutputManager] RENDER sheet updated successfully`);
        // Optional: Add verification for render sheet updates
      }

    } catch (error) {
      console.error(`[OutputManager] Error in updateSheetsWithVerticalUrls:`, error);
      // Consider if the enqueueSnackbar should be conditional or reflect partial success/failure
      enqueueSnackbar(`Error updating sheets: ${error instanceof Error ? error.message : String(error)}`, { variant: 'error' });
      // We might not want to re-throw here if one sheet update fails but the other could succeed, 
      // or if we want the polling to naturally stop without a hard error if columns are just missing.
      // For now, re-throwing will make it visible in the pollSocialClipStatus catch block.
      throw error; 
    }
  // Make sure to include renderSheetId in the dependency array if it's from component state/props and used in the callback
  }, [propSpreadsheetId, renderSheetId, enqueueSnackbar]); // Dependencies for useCallback, REMOVED sheetsService
  
  // Poll the social clip status from the API
  const pollSocialClipStatus = useCallback((jobId: string) => {
    console.log(`[OutputManager Polling] Starting polling sequence for Job ID: ${jobId}`);
    
    // Remove the updateSheetsWithVerticalUrls function from here, it's now defined at component level

    const checkStatus = async () => {
      // Check if polling should continue
      if (!isPollingSocialClipsRef.current) {
        console.log(`[OutputManager Polling] Polling stopped for Job ID: ${jobId}`);
        return;
      }
      
      try {
        console.log(`[OutputManager Polling] Checking status for Job ID: ${jobId}`);
        const response = await fetch(`${API_BASE_URL}/api/social-clip-status/${jobId}`);
        
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        
        // Add raw response debugging
        const responseText = await response.text();
        console.log(`[OutputManager Polling] Raw response for Job ${jobId}:`, responseText);
        
        // Parse the response as JSON after logging the raw text
        let jobStatus: SocialClipJobStatus; // Explicitly type jobStatus
        try {
          jobStatus = JSON.parse(responseText);
          console.log(`[OutputManager Polling] Parsed job status for Job ${jobId}:`, jobStatus);
        } catch (parseError) {
          console.error(`[OutputManager Polling] Error parsing response as JSON:`, parseError);
          throw new Error(`Failed to parse job status response: ${responseText}`);
        }
        
        // Update state based on job status
        if (jobStatus.status) {
          console.log(`[OutputManager Polling] Setting job status state to: ${jobStatus.status}`);
          setSocialClipJobStatus(jobStatus.status);
        }
        
        if (jobStatus.progress !== undefined) {
          console.log(`[OutputManager Polling] Setting job progress state to: ${jobStatus.progress}%`);
          setSocialClipJobProgress(jobStatus.progress);
        }
        
        if (jobStatus.error) { // Check if jobStatus.error is truthy (not null/undefined/empty string)
          console.log(`[OutputManager Polling] Adding error to state: ${jobStatus.error}`);
          setSocialClipErrors(prevErrors => Array.isArray(prevErrors) 
            ? [...prevErrors, jobStatus.error as string] // Add error if it exists
            : [jobStatus.error as string]);
        }
        
        // Handle job completion
        if (jobStatus.status === 'completed') {
          console.log(`[OutputManager Polling] Job ${jobId} completed successfully`);
          setIsPollingSocialClips(false); // Stop polling this job
          let sheetUpdateErrorOccurred = false;

          if (jobStatus.results && Array.isArray(jobStatus.results) && jobStatus.results.length > 0) {
            const successfulResults = jobStatus.results.filter(r => r.success && r.verticalUrl);
            if (successfulResults.length > 0) {
              if (MASTER_VIDEO_SHEET_ID && renderSheetId) {
                console.log(`[OutputManager Polling] ABOUT TO CALL updateSheetsWithVerticalUrls with target IDs: Master Video Sheet=${MASTER_VIDEO_SHEET_ID}, Render Sheet=${renderSheetId}`);
                console.log(`[OutputManager Polling] Vertical URLs to update: ${JSON.stringify(successfulResults.map((r: { position: number; verticalUrl: string; success: boolean }) => ({ position: r.position, url: r.verticalUrl })))}`);
                console.log(`[OutputManager Polling] Calling updateSheetsWithVerticalUrls at ${new Date().toISOString()}`);
                try {
                  await updateSheetsWithVerticalUrls(MASTER_VIDEO_SHEET_ID, renderSheetId, successfulResults);
                  console.log(`[OutputManager Polling] updateSheetsWithVerticalUrls completed successfully at ${new Date().toISOString()}`);
                  enqueueSnackbar(`Vertical clips generated and ${successfulResults.length} vertical URLs updated in sheets`, { variant: 'success' });
                } catch (updateFunctionError) {
                  sheetUpdateErrorOccurred = true;
                  console.error(`[OutputManager Polling] updateSheetsWithVerticalUrls FAILED with error:`, updateFunctionError);
                  // We will still proceed to assembly if vertical was selected, error is already enqueued by updateSheetsWithVerticalUrls
                }
              } else {
                sheetUpdateErrorOccurred = true;
                console.error(`[OutputManager Polling] ERROR - Missing spreadsheet IDs for sheet update: propSpreadsheetId=${propSpreadsheetId}, renderSheetId=${renderSheetId}`);
                enqueueSnackbar('Vertical clips generated but could not update sheets (missing spreadsheet IDs)', { variant: 'warning' });
              }
            } else {
              console.log(`[OutputManager Polling] No successful results with vertical URLs to update.`);
            }
          } else {
            console.log(`[OutputManager Polling] No results found in completed job, or results are not in expected format: ${JSON.stringify(jobStatus.results)}`);
          }

          // Centralized decision to call triggerFinalVideoAssembly
          if (outputFormat.includes('vertical')) {
            if(sheetUpdateErrorOccurred){
                 console.warn('[OutputManager Polling] Social clip job completed, but sheet update had issues. Attempting final video assembly for Vertical format with potentially stale/missing sheet URLs.');
            } else {
                 console.log('[OutputManager Polling] Social clip job completed (and sheets updated if necessary). Triggering final video assembly for Vertical format.');
            }
            // ---- TEMPORARY DELAY FOR DEBUGGING S3 PROPAGATION ----
            console.log('[OutputManager Polling] WAITING 10 SECONDS before triggering final video assembly...');
            setTimeout(async () => {
                console.log('[OutputManager Polling] 10-second delay OVER. Triggering final video assembly.');
                await triggerFinalVideoAssembly();
            }, 10000); // 10-second delay
            // ---- END TEMPORARY DELAY ----
            // Original direct call (now inside setTimeout): await triggerFinalVideoAssembly(); 
          }
          return; 
        }
        
        // Handle job failure
        if (jobStatus.status === 'failed') {
          console.log(`[OutputManager Polling] Job ${jobId} failed`);
          setIsPollingSocialClips(false);
          const errorMessage = jobStatus.error || 'Unknown error';
          setSocialClipErrors(prevErrors => Array.isArray(prevErrors) 
            ? [...prevErrors, errorMessage] 
            : [errorMessage]);
          setSocialClipJobStatus('failed');
          
          if (errorMessage.toLowerCase().includes('failed to download')) {
            console.warn(`[OutputManager Polling] Suppressed snackbar for job failure (includes 'failed to download'): ${errorMessage}`);
          } else {
            console.log(`[OutputManager Polling] Displaying snackbar for social clip error: ${errorMessage}`); // Added this log
            enqueueSnackbar(`Social clip generation failed: ${errorMessage}`, { variant: 'error' });
          }
          return; // No need to set another timeout
        }
        
        // If still processing, continue polling after a delay
        if (isPollingSocialClipsRef.current) {
          setTimeout(checkStatus, 2000); // Poll every 2 seconds
        }
      } catch (error) {
        console.error(`[OutputManager Polling] Error polling job status:`, error);
        setSocialClipErrors(prevErrors => Array.isArray(prevErrors) 
          ? [...prevErrors, `Polling error: ${error instanceof Error ? error.message : String(error)}`] 
          : [`Polling error: ${error instanceof Error ? error.message : String(error)}`]);
        
        // Try again if polling is still active, with a longer delay on error
        if (isPollingSocialClipsRef.current) {
          setTimeout(checkStatus, 5000); // Longer delay on error (5 seconds)
        }
      }
    };
    
    // Start polling immediately
    checkStatus();
    
    // Return a cleanup function
    return () => {
      // Nothing to clean up since we're using setTimeout and checking isPollingSocialClipsRef.current
    };
  }, [enqueueSnackbar, propSpreadsheetId, renderSheetId, updateSheetsWithVerticalUrls, outputFormat, triggerFinalVideoAssembly]); // ADDED outputFormat and triggerFinalVideoAssembly
  
  // Add the useEffect hook to start polling when conditions are met
  useEffect(() => {
    if (socialClipJobId && isPollingSocialClips) {
      // Only start polling if it hasn't been initiated for THIS job ID yet
      if (pollingInitiatedForJobRef.current !== socialClipJobId) {
        console.log(`[OutputManager useEffect Polling Trigger] Initiating poll for NEW Job ID: ${socialClipJobId}`);
        const cleanup = pollSocialClipStatus(socialClipJobId);
        pollingInitiatedForJobRef.current = socialClipJobId;
        
        // Return cleanup function
        return cleanup;
      } else {
        console.log(`[OutputManager useEffect Polling Trigger] Polling already initiated for Job ID: ${socialClipJobId}. Not restarting.`);
      }
    } else if (!socialClipJobId) {
      // Reset when there's no job ID
      pollingInitiatedForJobRef.current = null;
      // Ensure polling is explicitly stopped if job ID is removed
      if (isPollingSocialClips) {
        console.log('[OutputManager useEffect Polling Trigger] socialClipJobId removed, ensuring isPollingSocialClips is false.');
        setIsPollingSocialClips(false);
      }
    }
  }, [socialClipJobId, isPollingSocialClips, pollSocialClipStatus]);
  
  // Fetch VO data function
  const fetchVOData = useCallback(async (): Promise<void> => {
    if (!propSpreadsheetId) {
      setIsReadyForExport(false); // Not ready if no prop ID
      return;
    }
    setLoading(true);
    setError(null);
    setIsReadyForExport(false); // Set to false at the beginning of fetching

    // Ensure parentSpreadsheetId state is processed before proceeding
    // Note: parentSpreadsheetId is set by a separate useEffect.
    // We need to ensure this fetchVOData either runs after parentSpreadsheetId is set,
    // or that parentSpreadsheetId is passed to it or read directly if stable.
    // For now, we rely on the guard in handleExport for parentSpreadsheetId.

    try {
      console.log(`[OutputManager] Finding name for weekly spreadsheet ID: ${propSpreadsheetId}`);
      const weeklySheetName = await sheetsService.getFileName(propSpreadsheetId);
      console.log(`[OutputManager] Weekly spreadsheet name: ${weeklySheetName}`);

      const renderSheetName = `${weeklySheetName}-render`;
      console.log(`[OutputManager] Searching for render spreadsheet named: ${renderSheetName}`);
      const foundRenderId = await sheetsService.findFileByName(renderSheetName);

      if (!foundRenderId) {
        console.log(`[OutputManager] Render sheet "${renderSheetName}" not found. Creating new render sheet...`);
        
        // Find the most recent existing -render sheet in the Renders folder to copy
        const RENDERS_FOLDER_ID = '1WYxCqZA9GntggbYfDC3cUe6PTGqwgQQR';
        const renderFiles = await sheetsService.listFiles(RENDERS_FOLDER_ID);
        const { parse, isValid } = await import('date-fns');
        const allRenderSheets = renderFiles
          .map((file: any) => {
            if (!file.name?.endsWith('-render') || !file.id) return null;
            const datePart = file.name.replace('-render', '');
            const date = parse(datePart, 'M-d-yy', new Date());
            return isValid(date) ? { id: String(file.id), date: date, name: String(file.name) } : null;
          })
          .filter((item: any) => item !== null)
          .sort((a: any, b: any) => b.date.getTime() - a.date.getTime());

        if (allRenderSheets.length === 0) {
          throw new Error('Cannot create render sheet: No previous "-render" sheets found in the folder to copy from.');
        }

        const latestRenderSheet = allRenderSheets[0];
        if (!latestRenderSheet) {
          throw new Error('No valid render sheet found to copy from.');
        }
        console.log(`[OutputManager] Found latest render sheet to copy: ${latestRenderSheet.name} (ID: ${latestRenderSheet.id})`);

        // Copy the latest sheet
        const copiedFile = await sheetsService.copyFile(latestRenderSheet.id, RENDERS_FOLDER_ID);
        console.log(`[OutputManager] Copied sheet, new ID: ${copiedFile.id}, temp name: ${copiedFile.name}`);

        // Rename the copy
        if (!copiedFile.id) {
          throw new Error("Copy operation did not return a file ID.");
        }
        await sheetsService.renameFile(copiedFile.id, renderSheetName);
        const newRenderId = copiedFile.id;
        console.log(`[OutputManager] Renamed copied sheet to: ${renderSheetName}. New ID: ${newRenderId}`);

        // Set currentSpreadsheetId to the current weekly sheet before navigating
        localStorage.setItem('currentSpreadsheetId', propSpreadsheetId);
        console.log(`[OutputManager] Set currentSpreadsheetId to weekly sheet: ${propSpreadsheetId}`);
        
        // Navigate to Intro/Outro page with the new render sheet ID and sheet name
        console.log(`[OutputManager] Navigating to /intro-outro/${newRenderId} with sheet name: ${renderSheetName}`);
        navigate(`/intro-outro/${newRenderId}`, { 
          state: { sheetName: renderSheetName }
        });
        return;
      }
      console.log(`[OutputManager] Found render spreadsheet ID: ${foundRenderId}`);
      setRenderSheetId(foundRenderId);

      // --- Start: Copy data from Weekly to Render sheet ---
      const sourceSpreadsheetId = propSpreadsheetId;
      const targetSpreadsheetId = foundRenderId;
      const sheetToCopy = 'Sheet1'; // Assuming the relevant data is always in Sheet1
      const rangeToCopy = `${sheetToCopy}!A1:Z13`; // Copy first 13 rows, columns A-Z

      try {
          console.log(`[OutputManager] Starting copy from ${sourceSpreadsheetId} (${rangeToCopy}) to ${targetSpreadsheetId} (${rangeToCopy})`);
          await sheetsService.copyRange(sourceSpreadsheetId, rangeToCopy, targetSpreadsheetId, rangeToCopy);
          console.log(`[OutputManager] Successfully copied data from weekly to render sheet.`);
      } catch (copyError) {
          console.error(`[OutputManager] Failed to copy data from weekly sheet to render sheet:`, copyError);
          setError(`Failed to synchronize render sheet: ${copyError instanceof Error ? copyError.message : String(copyError)}`);
          setLoading(false);
          setRenderSheetId(null);
          return; // Stop execution if copy fails
      }
      // --- End: Copy data ---

      const targetSheetName = 'Sheet1'; // Still fetch from Sheet1 of the render sheet

      console.log(`[OutputManager] Fetching data for spreadsheet: ${targetSpreadsheetId}, Target Sheet: ${targetSheetName}`);

      const headers = await sheetsService.getHeaders(targetSpreadsheetId, targetSheetName);

      let voTextColIndex = headers.findIndex((h: string) => h?.toLowerCase().includes('vo text'));
      let voInColIndex = headers.findIndex((h: string) => h?.toLowerCase().includes('vo in'));
      let voOutColIndex = headers.findIndex((h: string) => h?.toLowerCase().includes('vo out'));
      let songTitleColIndex = headers.findIndex((h: string) => h?.toLowerCase() === 'song');
      let songArtistColIndex = headers.findIndex((h: string) => h?.toLowerCase() === 'artist');
      let voUrlColIndex = headers.findIndex((h: string) => h?.toLowerCase().includes('voice over url'));

      // Find indices for social clip data
      let foundS3UrlColIndex = headers.findIndex((h: string) => h === 'S3 URL');
      let foundInPointColIndex = headers.findIndex((h: string) => h === 'IN Point'); // Video IN Point
      let foundDurationColIndex = headers.findIndex((h: string) => h === 'Duration'); // Video Duration
      let foundPositionColIndex = headers.findIndex((h: string) => h === 'Position');

      voTextColIndex = voTextColIndex === -1 ? 11 : voTextColIndex;
      voInColIndex = voInColIndex === -1 ? 9 : voInColIndex;
      voOutColIndex = voOutColIndex === -1 ? 10 : voOutColIndex;
      songTitleColIndex = songTitleColIndex === -1 ? 2 : songTitleColIndex;
      songArtistColIndex = songArtistColIndex === -1 ? 3 : songArtistColIndex;
      voUrlColIndex = voUrlColIndex === -1 ? 12 : voUrlColIndex;

      // Set state for social clip column indices
      setS3UrlColIndex(foundS3UrlColIndex === -1 ? null : foundS3UrlColIndex);
      setInPointColIndex(foundInPointColIndex === -1 ? null : foundInPointColIndex);
      setDurationColIndex(foundDurationColIndex === -1 ? null : foundDurationColIndex);
      setPositionColIndex(foundPositionColIndex === -1 ? null : foundPositionColIndex);

      // Log the found indices (or null) for social clips
      console.log(`[OutputManager] Found social clip indices - S3 URL: ${foundS3UrlColIndex}, Video IN Point: ${foundInPointColIndex}, Video Duration: ${foundDurationColIndex}, Position: ${foundPositionColIndex}`);

      // --- Start: Re-add local column letter variables ---
      const voTextCol = String.fromCharCode(65 + voTextColIndex);
      const voInCol = String.fromCharCode(65 + voInColIndex);
      const voOutCol = String.fromCharCode(65 + voOutColIndex);
      const songTitleCol = String.fromCharCode(65 + songTitleColIndex);
      const songArtistCol = String.fromCharCode(65 + songArtistColIndex);
      const voUrlCol = String.fromCharCode(65 + voUrlColIndex);
      
      // Get column letters for social clip data IF indices are valid (used for fetching)
      const s3Col = foundS3UrlColIndex !== -1 ? String.fromCharCode(65 + foundS3UrlColIndex) : null;
      const videoInColLetter = foundInPointColIndex !== -1 ? String.fromCharCode(65 + foundInPointColIndex) : null;
      const videoDurationColLetter = foundDurationColIndex !== -1 ? String.fromCharCode(65 + foundDurationColIndex) : null;
      const posColLetter = foundPositionColIndex !== -1 ? String.fromCharCode(65 + foundPositionColIndex) : null;
      // --- End: Re-add local column letter variables ---
      
      const ranges = [];
      for (let row = 2; row <= 13; row++) { // Covers Intro, 10-1, Outro
        ranges.push(`${targetSheetName}!${voTextCol}${row}`);
        ranges.push(`${targetSheetName}!${voInCol}${row}`);
        ranges.push(`${targetSheetName}!${voOutCol}${row}`);
        ranges.push(`${targetSheetName}!${songTitleCol}${row}`);
        ranges.push(`${targetSheetName}!${songArtistCol}${row}`);

        // Add ranges for social clip data only for rows 2-11 (Positions 10 to 1)
        // These use the local s3Col, videoInColLetter etc.
        if (row >= 2 && row <= 11) {
          if (s3Col) ranges.push(`${targetSheetName}!${s3Col}${row}`);
          if (videoInColLetter) ranges.push(`${targetSheetName}!${videoInColLetter}${row}`);
          if (videoDurationColLetter) ranges.push(`${targetSheetName}!${videoDurationColLetter}${row}`);
          if (posColLetter) ranges.push(`${targetSheetName}!${posColLetter}${row}`);
        }
      }
      
      const dateCellRange = `${targetSheetName}!B28`;
      ranges.push(dateCellRange);
      
      const batchData = await sheetsService.batchGetValues(targetSpreadsheetId, ranges);
      
      const newVoInPoints: { [key: string]: string } = {};
      const newVoOutPoints: { [key: string]: string } = {};
      const newSongTitles: { [key: string]: string } = {};
      const newSongArtists: { [key: string]: string } = {};
      // --- Start: Ensure social clip data objects are initialized ---
      const newS3Urls: { [key: string]: string } = {};
      const newVideoInPoints: { [key: string]: string } = {};
      const newVideoDurations: { [key: string]: string } = {};
      const newChartPositions: { [key: string]: string } = {};
      // --- End: Ensure social clip data objects are initialized ---
      
      const rowToKeyMap: { [key: number]: string } = {
        2: '10', 3: '9', 4: '8', 5: '7', 6: '6', 7: '5', 
        8: '4', 9: '3', 10: '2', 11: '1', 12: 'Intro', 13: 'Outro'
      };
      
      for (let row = 2; row <= 13; row++) {
        const key = rowToKeyMap[row] || `row-${row}`;
        const textRange = `${targetSheetName}!${voTextCol}${row}`;
        const inRange = `${targetSheetName}!${voInCol}${row}`;
        const outRange = `${targetSheetName}!${voOutCol}${row}`;
        const titleRange = `${targetSheetName}!${songTitleCol}${row}`;
        const artistRange = `${targetSheetName}!${songArtistCol}${row}`;
        
        // Ranges for social clip data (for rows 2-11), using local column letters
        const s3Range = (row >= 2 && row <= 11 && s3Col) ? `${targetSheetName}!${s3Col}${row}` : null;
        const videoInRange = (row >= 2 && row <= 11 && videoInColLetter) ? `${targetSheetName}!${videoInColLetter}${row}` : null;
        const videoDurationRange = (row >= 2 && row <= 11 && videoDurationColLetter) ? `${targetSheetName}!${videoDurationColLetter}${row}` : null;
        const posRange = (row >= 2 && row <= 11 && posColLetter) ? `${targetSheetName}!${posColLetter}${row}` : null;
        
        const textData = batchData[textRange];
        const inData = batchData[inRange];
        const outData = batchData[outRange];
        const titleData = batchData[titleRange];
        const artistData = batchData[artistRange];
        
        const voText = textData?.values?.[0]?.[0] || '';
        const inPoint = inData?.values?.[0]?.[0] || '';
        const outPoint = outData?.values?.[0]?.[0] || '';
        const songTitle = titleData?.values?.[0]?.[0] || '';
        const songArtist = artistData?.values?.[0]?.[0] || '';
        
        // Get social clip data values
        const s3UrlValue = s3Range ? batchData[s3Range]?.values?.[0]?.[0] || '' : '';
        const videoInPointValue = videoInRange ? batchData[videoInRange]?.values?.[0]?.[0] || '' : '';
        const videoDurationValue = videoDurationRange ? batchData[videoDurationRange]?.values?.[0]?.[0] || '' : '';
        const chartPositionValue = posRange ? batchData[posRange]?.values?.[0]?.[0] || '' : '';
        
        if (voText || key === 'Intro' || key === 'Outro') {
          newVoInPoints[key] = inPoint;
          newVoOutPoints[key] = outPoint;
          newSongTitles[key] = songTitle;
          newSongArtists[key] = songArtist;
        }

        // Store social clip data for positions 10 to 1 (keys '10' through '1')
        if (key !== 'Intro' && key !== 'Outro') {
          if (foundS3UrlColIndex !== -1) newS3Urls[key] = s3UrlValue; // Use found...Index to decide if data should be stored
          if (foundInPointColIndex !== -1) newVideoInPoints[key] = videoInPointValue;
          if (foundDurationColIndex !== -1) newVideoDurations[key] = videoDurationValue;
          if (foundPositionColIndex !== -1) newChartPositions[key] = chartPositionValue;
        }
      }
      
      const dateData = batchData[dateCellRange];
      const extractedDate = dateData?.values?.[0]?.[0] || null;
      setSheetDate(extractedDate);
      console.log(`[OutputManager] Extracted sheet date from B28: ${extractedDate}`);
      
      setVoInPoints(newVoInPoints);
      setVoOutPoints(newVoOutPoints);
      setSongTitles(newSongTitles);
      setSongArtists(newSongArtists);
      // --- Start: Set state for social clip data ---
      setS3Urls(newS3Urls);
      setVideoInPoints(newVideoInPoints);
      setVideoDurations(newVideoDurations);
      setChartPositions(newChartPositions);
      // --- End: Set state for social clip data ---

      const storedAudioData = localStorage.getItem(`processedAudioData_${propSpreadsheetId}`);
      if (storedAudioData) {
          const { url } = JSON.parse(storedAudioData);
          setProcessedAudioUrl(url || null);
          console.log('[OutputManager] Loaded processed audio URL from localStorage:', url);
      } else {
          console.log('[OutputManager] No processed audio URL found in localStorage.');
      }

      // Check if all required IDs are set before declaring ready
      if (foundRenderId && extractedDate && parentSpreadsheetId) { // Added parentSpreadsheetId check
        setIsReadyForExport(true);
        console.log('[OutputManager] fetchVOData successful: isReadyForExport set to true.', { foundRenderId, extractedDate, parentSpreadsheetId });
      } else {
        setIsReadyForExport(false);
        let missing = [];
        if (!foundRenderId) missing.push("renderSheetId");
        if (!extractedDate) missing.push("sheetDate");
        if (!parentSpreadsheetId) missing.push("parentSpreadsheetId (from URL/prop)"); // Log if parentSpreadsheetId is missing
        console.warn(`[OutputManager] fetchVOData completed but not ready. Missing: ${missing.join(', ')}`);
        setError(`Initialization incomplete. Missing: ${missing.join(', ')}.`);
      }

    } catch (err: any) {
      console.error('[OutputManager] Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load required VO data');
      if (err?.message && err.message.includes('403')) {
          setError('Permission error fetching data from Google Sheet. Please check access rights.');
      }
      setRenderSheetId(null);
      setIsReadyForExport(false); // Ensure false on error
    } finally {
      setLoading(false);
    }
  }, [propSpreadsheetId, parentSpreadsheetId]); // Added parentSpreadsheetId to dependencies of fetchVOData

  useEffect(() => {
    if (propSpreadsheetId && parentSpreadsheetId) {
      // Only call fetchVOData when both propSpreadsheetId AND parentSpreadsheetId are available
      console.log('[OutputManager] Both propSpreadsheetId and parentSpreadsheetId are available, calling fetchVOData');
      fetchVOData();
    } else if (propSpreadsheetId && !parentSpreadsheetId) {
      console.log('[OutputManager] propSpreadsheetId available but waiting for parentSpreadsheetId from URL query');
      setIsReadyForExport(false);
    } else {
      console.log('[OutputManager] No propSpreadsheetId, clearing all state');
      setVoInPoints({});
      setVoOutPoints({});
      setSongTitles({});
      setSongArtists({});
      setProcessedAudioUrl(null);
      setLineAudioFiles({});
      setError(null);
      setLoading(false);
      setRenderSheetId(null);
      // --- Start: Clear social clip state if no spreadsheetId ---
      setS3UrlColIndex(null);
      setInPointColIndex(null);
      setDurationColIndex(null);
      setPositionColIndex(null);
      setS3Urls({});
      setVideoInPoints({});
      setVideoDurations({});
      setChartPositions({});
      // --- End: Clear social clip state if no spreadsheetId ---
      // --- Start: Clear social clip polling state if no spreadsheetId ---
      setSocialClipJobId(null);
      setSocialClipJobStatus(null);
      setSocialClipJobProgress(null);
      setSocialClipErrors([]);
      setIsPollingSocialClips(false);
      // --- End: Clear social clip polling state if no spreadsheetId ---
      setIsReadyForExport(false); // Also set to false if no propId
    }
  }, [propSpreadsheetId, parentSpreadsheetId, fetchVOData]); // Added parentSpreadsheetId as dependency
  
  // Basic useEffect to simulate loading
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleCancelGeneration = useCallback(() => {
    console.log('[OutputManager] Cancelling generation process.');
    setIsExporting(false);
    setIsPollingSocialClips(false); // This will update isPollingSocialClipsRef via its own useEffect
    
    setSocialClipJobId(null);
    setSocialClipJobStatus('cancelled'); 
    setSocialClipJobProgress(null);
    setSocialClipErrors([]);
    
    enqueueSnackbar('Generation process cancelled by user.', { variant: 'warning' });
  }, [enqueueSnackbar]);
  
  // Function to fetch sheet data and build clip instructions
  const buildClipInstructions = async (spreadsheetId: string): Promise<ClipInstruction[]> => {
    try {
      console.log('[OutputManager] Fetching data to build clip instructions');
      
      const result = await sheetsService.getData(spreadsheetId, `Sheet1!A1:Z1000`);
      
      if (!result.values || result.values.length < 2) {
        throw new Error('No data found in the spreadsheet');
      }
      
      const headers = result.values[0];
      const rows = result.values.slice(1);
      
      // Find column indices for the required fields
      const positionIndex = headers.findIndex(header => header.toLowerCase() === 'position');
      const s3UrlIndex = headers.findIndex(header => header.toLowerCase() === 's3 url');
      const artistIndex = headers.findIndex(header => header.toLowerCase() === 'artist');
      const songIndex = headers.findIndex(header => header.toLowerCase() === 'song');
      const voInIndex = headers.findIndex(header => header.toLowerCase() === 'vo in');
      const voOutIndex = headers.findIndex(header => header.toLowerCase() === 'vo out');
      const inPointIndex = headers.findIndex(header => header.toLowerCase() === 'in point');
      const durationIndex = headers.findIndex(header => header.toLowerCase() === 'duration');
      
      // Validate all required indices are found
      if (positionIndex === -1 || s3UrlIndex === -1 || artistIndex === -1 || songIndex === -1 || 
          voInIndex === -1 || voOutIndex === -1 || inPointIndex === -1 || durationIndex === -1) {
        throw new Error('Required columns not found in the spreadsheet');
      }
      
      const clipInstructions: ClipInstruction[] = [];
      
      // Process each row to create clip instructions
      for (const row of rows) {
        const position = parseInt(row[positionIndex], 10);
        const s3Url = row[s3UrlIndex];
        
        // Skip rows without position or S3 URL
        if (isNaN(position) || !s3Url) {
          continue;
        }
        
        clipInstructions.push({
          position,
          s3Url,
          artist: row[artistIndex] || '',
          song: row[songIndex] || '',
          voIn: row[voInIndex] || '00:00:00',
          voOut: row[voOutIndex] || '00:00:00',
          inPoint: row[inPointIndex] || '00:00:00',
          duration: row[durationIndex] || '00:00:00'
        });
      }
      
      return clipInstructions;
    } catch (error) {
      console.error('[OutputManager] Error building clip instructions:', error);
      throw error;
    }
  };
  
  const handleExport = async (): Promise<void> => {
    let sieveJobWasInitiated = false; 

    // === COMPREHENSIVE DEBUG LOGGING ===
    console.log('[OutputManager Export] === STARTING EXPORT DEBUG ===');
    console.log('[OutputManager Export] isReadyForExport:', isReadyForExport);
    console.log('[OutputManager Export] parentSpreadsheetId:', parentSpreadsheetId);
    console.log('[OutputManager Export] renderSheetId:', renderSheetId);
    console.log('[OutputManager Export] sheetDate:', sheetDate);
    console.log('[OutputManager Export] propSpreadsheetId:', propSpreadsheetId);
    
    // Check all localStorage keys related to audioExportSettings
    const allKeys = Object.keys(localStorage);
    const audioKeys = allKeys.filter(key => key.includes('audioExportSettings'));
    console.log('[OutputManager Export] All localStorage keys containing "audioExportSettings":', audioKeys);
    
    // Show what each key contains
    audioKeys.forEach(key => {
      const value = localStorage.getItem(key);
      console.log(`[OutputManager Export] localStorage["${key}"] =`, value);
    });

    // CRITICAL GUARD CLAUSE
    if (!isReadyForExport || !parentSpreadsheetId || !renderSheetId || !sheetDate) {
      enqueueSnackbar('Component is not ready or critical data is missing. Please wait or refresh.', { variant: 'warning' });
      console.warn('[OutputManager Export] Aborted: Not ready or missing critical data.', { 
        isReadyForExport, 
        parentSpreadsheetId, // Log this to see its value
        renderSheetId, 
        sheetDate 
      });
      setIsExporting(false); // Reset button state
      return;
    }
    
    // This check was originally here, ensure it's still relevant or covered by isReadyForExport
    // if (!propSpreadsheetId || !renderSheetId) { 
    //   enqueueSnackbar('Spreadsheet information is missing.', { variant: 'error' });
    //   return;
    // }

    setValidationErrors([]);
    setIsExporting(true);
    setError(null);
    setSocialClipErrors([]);
    setSocialClipJobStatus(null); 
    setMovieProjects([]);
    setIsPollingMovieStatus(false);
    
    try {
      console.log(`[OutputManager Validation] Checking Intro/Outro fields in sheet: ${renderSheetId}`);
      const validationRange1 = 'Sheet1!A17:B26';
      const validationRange2 = 'Sheet1!A37:B48';
      
      const [validationData1, validationData2] = await Promise.all([
        sheetsService.getData(renderSheetId, validationRange1),
        sheetsService.getData(renderSheetId, validationRange2)
      ]);
      
      const rows1 = validationData1.values || [];
      const rows2 = validationData2.values || [];
      const allRows = [...rows1, ...rows2];
      
      const foundValues: { [key: string]: string } = {};

      allRows.forEach((row: string[]) => {
        const label = row[0]?.trim();
        const value = row[1]?.trim();
        if (label) {
          foundValues[label] = value || '';
        }
      });
      console.log("[OutputManager Validation] Found values from combined ranges:", foundValues);

      const missingFields: string[] = [];
      for (const label in REQUIRED_INTRO_OUTRO_FIELDS) {
        if (foundValues[label] === undefined || foundValues[label] === '') {
          const friendlyName = REQUIRED_INTRO_OUTRO_FIELDS[label];
          if (label.endsWith('_url')) {
            missingFields.push(`${friendlyName} is missing a URL.`);
          } else if (label.endsWith('_seek')) {
            missingFields.push(`${friendlyName} is missing a Seek In Point.`);
          } else {
             missingFields.push(`${friendlyName} is missing a value.`);
          }
        }
      }

      if (missingFields.length > 0) {
        console.log("[OutputManager Validation] Missing fields:", missingFields);
        setValidationErrors(missingFields);
        setValidationDialogOpen(true);
        setIsExporting(false);
        return;
      }

      console.log('[OutputManager Validation] All required Intro/Outro fields are present.');
    } catch (validationError: any) {
      console.error('[OutputManager Validation] Error fetching or checking Intro/Outro fields:', validationError);
      setError(`Validation Error: Failed to check required fields - ${validationError.message}`);
      setIsExporting(false);
      return;
    }

    let outroSkipped = false;
    try {
      console.log(`[OutputManager Export] Starting export for sheet: ${propSpreadsheetId}, Render Sheet: ${renderSheetId}, Date: ${sheetDate}`);

      let formattedSheetDate = 'unknown-date';
      if (sheetDate) {
        try {
          const dateObj = new Date(sheetDate);
          if (!isNaN(dateObj.getTime())) {
            const month = dateObj.getMonth() + 1;
            const day = dateObj.getDate();
            const year = dateObj.getFullYear().toString().slice(-2);
            formattedSheetDate = `${month}-${day}-${year}`;
            console.log(`[OutputManager Export] Formatted sheet date from '${sheetDate}' to '${formattedSheetDate}'`);
          } else {
            console.warn(`[OutputManager Export] Could not parse sheet date string '${sheetDate}'. Using default.`);
          }
        } catch (dateError) {
          console.error(`[OutputManager Export] Error parsing sheet date '${sheetDate}':`, dateError);
        }
      } else {
        console.warn('[OutputManager Export] sheetDate state is null or empty. Using default.');
      }

      // Use parentSpreadsheetId to load audio export settings
      const audioSettingsStorageKey = `audioExportSettings_${parentSpreadsheetId}`;
      console.log('[OutputManager Export] Constructed localStorage key:', audioSettingsStorageKey);
      console.log('[OutputManager Export] parentSpreadsheetId value used in key:', parentSpreadsheetId);
      console.log('[OutputManager Export] typeof parentSpreadsheetId:', typeof parentSpreadsheetId);
      
      const savedSettingsStr = localStorage.getItem(audioSettingsStorageKey);
      console.log('[OutputManager Export] Raw localStorage value for key:', savedSettingsStr);
      let actualMakeupGain = '0'; // Default gain if nothing is found or parse fails

      if (savedSettingsStr) {
          try {
              const parsedSettings = JSON.parse(savedSettingsStr);
              // VOManager saves it as makeupGain directly now.
              // It might also be under an old 'gain' key if settings are very old.
              if (parsedSettings.makeupGain !== undefined) {
                  actualMakeupGain = String(parsedSettings.makeupGain);
              } else if (parsedSettings.gain !== undefined) { // Fallback for very old format
                  actualMakeupGain = String(parsedSettings.gain);
              }
              console.log(`[OutputManager Export] Loaded makeupGain '${actualMakeupGain}' from localStorage key ${audioSettingsStorageKey}`);
          } catch (e) {
              console.error(`[OutputManager Export] Failed to parse settings from localStorage key ${audioSettingsStorageKey}. Using default makeupGain '0'.`, e);
          }
      } else {
           console.log(`[OutputManager Export] No export settings found in localStorage key ${audioSettingsStorageKey}, using default makeupGain '0'.`);
      }
      
      // Prepare settings for the backend. Include ALL settings from localStorage.
      let audioSettingsForPayload: { makeupGain: string; [key: string]: string } = {
          makeupGain: actualMakeupGain 
      };
      
      // Add any line-specific gains from the parsed settings
      if (savedSettingsStr) {
          try {
              const parsedSettings = JSON.parse(savedSettingsStr);
              // Copy any lineGain_* properties to the payload
              Object.keys(parsedSettings).forEach(key => {
                  if (key.startsWith('lineGain_')) {
                      audioSettingsForPayload[key] = parsedSettings[key];
                  }
              });
          } catch (e) {
              console.error(`[OutputManager Export] Failed to parse settings for line-specific gains:`, e);
          }
      }
      
      // This console log below is for the variable that will be used in the payload
      console.log('[OutputManager Export] Final audioExportSettings to be sent to backend:', audioSettingsForPayload);

      // --- Start: Check for missing VO Text and generate silent audio files ---
      console.log('[OutputManager Export] Checking for missing VO Text and generating silent audio files...');
      
      // Fetch VO Text data from the sheet to check what's missing
      const headers = await sheetsService.getHeaders(renderSheetId, 'Sheet1');
      console.log('[OutputManager Export] Headers found:', headers);
      
      // Look for exact column names
      const voTextColIndex = headers.findIndex((h: string) => h === 'Voice Over Text');
      const voUrlColIndex = headers.findIndex((h: string) => h === 'Voice Over URL');
      
      console.log(`[OutputManager Export] Found VO Text column at index: ${voTextColIndex}`);
      console.log(`[OutputManager Export] Found VO URL column at index: ${voUrlColIndex}`);
      
      if (voTextColIndex === -1 || voUrlColIndex === -1) {
        console.warn('[OutputManager Export] Could not find VO Text or VO URL columns');
        console.warn('[OutputManager Export] Available headers:', headers);
      } else {
        const voTextCol = String.fromCharCode(65 + voTextColIndex);
        const voUrlCol = String.fromCharCode(65 + voUrlColIndex);
        
        // Map positions to rows
        const positionToRow: { [key: string]: number } = {
          'Intro': 12,
          '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
          '5': 7, '4': 8, '3': 9, '2': 10, '1': 11,
          'Outro': 13
        };
        
        const silentAudioPromises: Promise<void>[] = [];
        
        for (const key of DISPLAY_ORDER) {
          if (key === 'Outro') continue; // Skip outro as it uses default outro handling
          
          const row = positionToRow[key];
          if (!row) continue;
          
          // Get VO Text for this position
          const voTextRange = `Sheet1!${voTextCol}${row}`;
          const voTextData = await sheetsService.getData(renderSheetId, voTextRange);
          const voText = voTextData?.values?.[0]?.[0] || '';
          
          console.log(`[OutputManager Export] Checking ${key} (row ${row}): VO Text = "${voText}", trimmed = "${voText.trim()}", isEmpty = ${!voText.trim()}`);
          
          // If no VO Text, copy the existing silence file
          if (!voText.trim()) {
            console.log(`[OutputManager Export] Position ${key} has no VO Text, copying silence file...`);
            
            // Determine filename based on position
            const filename = key === 'Intro' ? 'intro.wav' : `${key}.wav`;
            
            // Create promise for copying the silence file
            const silentAudioPromise = (async () => {
              try {
                // Copy the existing silence file to the appropriate location
                const response = await fetch('/api/copy-silence-file', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    filename: filename,
                    sheetDate: formattedSheetDate
                  })
                });
                
                if (!response.ok) {
                  throw new Error(`Failed to copy silence file for ${key}: ${response.statusText}`);
                }
                
                const result = await response.json();
                console.log(`[OutputManager Export] Copied silence file for ${key}: ${result.s3Url}`);
                
              } catch (error) {
                console.error(`[OutputManager Export] Failed to copy silence file for ${key}:`, error);
                throw error;
              }
            })();
            
            silentAudioPromises.push(silentAudioPromise);
          }
        }
        
        // Wait for all silence file copying to complete
        if (silentAudioPromises.length > 0) {
          console.log(`[OutputManager Export] Copying ${silentAudioPromises.length} silence files...`);
          await Promise.all(silentAudioPromises);
          console.log('[OutputManager Export] All silence files copied successfully');
        }
      }
      // --- End: Generate silent audio files ---

      const exportVoData = DISPLAY_ORDER.map(key => {
        const inPointStr = String(voInPoints[key] || '');
        const outPointStr = String(voOutPoints[key] || '');

        let inPoint = parseFloat(inPointStr || '0');
        let outPoint = parseFloat(outPointStr || '0');
        
        // Check if this is a silent audio file (no VO Text, so In/Out points are 0 or empty)
        const isSilentAudio = (!inPointStr || inPointStr.trim() === '' || inPointStr === '0') && 
                             (!outPointStr || outPointStr.trim() === '' || outPointStr === '0');
        
        console.log(`[OutputManager Export] Processing ${key}: inPointStr="${inPointStr}", outPointStr="${outPointStr}", isSilentAudio=${isSilentAudio}`);
        console.log(`[OutputManager Export] ${key} parsed values: inPoint=${inPoint}, outPoint=${outPoint}`);
        
        let audioUrl;
        if (isSilentAudio && key !== 'Outro') {
          // Use the silence file URL for positions without VO Text
          const filename = key === 'Intro' ? 'intro.wav' : `${key}.wav`;
          audioUrl = `https://c1-scott-scratchdisk.s3.us-west-2.amazonaws.com/Billboard/VO/${formattedSheetDate}/${filename}`;
          console.log(`[OutputManager Export] Position ${key} using silence file: ${audioUrl}`);
          
          inPoint = 0;
          // Set duration based on position type
          if (key === 'Intro') {
            outPoint = 5; // 5 seconds for Intro
          } else {
            outPoint = 3; // 3 seconds for positions 1-10
          }
        } else {
          // Use line audio file or processed audio for positions with VO Text
          audioUrl = lineAudioFiles[key] || processedAudioUrl;
          
          if (isSilentAudio) {
            console.log(`[OutputManager Export] Position ${key} appears to be silent audio, setting appropriate In/Out points`);
            inPoint = 0;
            // Set duration based on position type
            if (key === 'Intro') {
              outPoint = 5; // 5 seconds for Intro
            } else if (key === 'Outro') {
              // Keep Outro logic as is - it should have proper In/Out points
              outPoint = 0;
            } else {
              outPoint = 3; // 3 seconds for positions 1-10
            }
          }
        }
        
        console.log(`[OutputManager Export] Final values for ${key}: inPoint=${inPoint}, outPoint=${outPoint}, audioUrl=${audioUrl}`);  
        
        if (isNaN(inPoint) || isNaN(outPoint)) {
          console.warn(`[OutputManager Export] Invalid (non-numeric) value found for key '${key}': IN='${inPointStr}', OUT='${outPointStr}'. Value will be treated as NaN.`);
          if (isNaN(parseFloat(inPointStr))) inPoint = NaN;
          if (isNaN(parseFloat(outPointStr))) outPoint = NaN;
        }

        console.log(`[OutputManager Export] About to check filtering for ${key}: inPoint=${inPoint}, outPoint=${outPoint}, audioUrl=${audioUrl}`);

        if (key === 'Outro' && (isNaN(inPoint) || isNaN(outPoint))) {
          console.log(`[OutputManager Export] Outro has invalid points (In: ${inPointStr}, Out: ${outPointStr}). It will be skipped for export and sheet will be updated with defaults.`);
          outroSkipped = true;
          return null;
        }

        return {
          key,
          audioUrl: audioUrl ? (audioUrl.startsWith('http') ? audioUrl : `${API_BASE_URL}${audioUrl}`) : null,
          inPoint,
          outPoint,
          songTitle: songTitles[key] || '',
          artist: songArtists[key] || ''
        };
      }).filter((vo): vo is Exclude<typeof vo, null> => {
        if (vo === null) {
            console.log(`[OutputManager Export] Filtering out ${vo} (null)`);
            return false;
        }

        console.log(`[OutputManager Export] Checking filters for key '${vo.key}': inPoint=${vo.inPoint}, outPoint=${vo.outPoint}, audioUrl=${vo.audioUrl}`);

        if (!vo.audioUrl) {
            console.warn(`[OutputManager Export] Skipping key '${vo.key}' due to missing audio URL.`);
            return false;
        }
        if (isNaN(vo.inPoint) || isNaN(vo.outPoint)) {
            console.warn(`[OutputManager Export] Skipping key '${vo.key}' due to invalid (NaN) inPoint or outPoint.`);
            return false;
        }
        // Additional check to prevent 0,0 combinations that server considers invalid
        // But allow Intro with 0,5 (0 inPoint, 5 second duration)
        if (vo.inPoint === 0 && vo.outPoint === 0) {
            console.warn(`[OutputManager Export] Skipping key '${vo.key}' due to invalid 0,0 inPoint/outPoint combination.`);
            return false;
        }
        console.log(`[OutputManager Export] Including key '${vo.key}' in export: inPoint=${vo.inPoint}, outPoint=${vo.outPoint}, audioUrl=${vo.audioUrl}`);
        return true;
    });

    console.log(`[OutputManager Export] Prepared ${exportVoData.length} VO items for export.`);
    if (exportVoData.length === 0 && !outroSkipped) {
        throw new Error('No valid VO clips found to export. Check audio URLs and spreadsheet data.');
    } else if (exportVoData.length === 0 && outroSkipped) {
        console.log("[OutputManager Export] No clips to export, but Outro was skipped. Will proceed to update sheet if needed.");
    }

    const payload = {
      spreadsheetId: propSpreadsheetId,
      sheetDate: formattedSheetDate,
      voData: exportVoData,
      audioSettings: audioSettingsForPayload, // Use the correctly scoped variable here
    };

    if (exportVoData.length > 0) {
        const response = await fetch('/api/export-vo-files', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          let errorBody = `Failed to export VO files (Status ${response.status})`;
          try {
              // Try to parse as JSON first, as that's the expected error format from the backend
              const errorJson = await response.json();
              errorBody = errorJson.error || errorJson.message || JSON.stringify(errorJson);
          } catch (jsonParseError) {
              // If JSON parsing fails, then try to read as text
              try {
                errorBody = await response.text(); // This might be empty if already read or not text
              } catch (textReadError) {
                console.error('[OutputManager Export] Error reading response text after JSON parse failed:', textReadError);
                // errorBody remains the generic status message
              }
          }
          throw new Error(errorBody);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Export failed on server');
        }

        let successCount = 0;
        let totalCount = 0;
        if (result.results && Array.isArray(result.results)) {
            successCount = result.results.filter((r: { success: boolean }) => r.success).length;
            totalCount = result.results.length;
        } else {
            successCount = exportVoData.length;
            totalCount = exportVoData.length;
            console.warn('[OutputManager Export] Server response did not include detailed results array.');
        }

        if (totalCount === 0) {
             enqueueSnackbar('No clips were attempted for export.', { variant: 'warning' });
        } else if (successCount === totalCount) {
          enqueueSnackbar(`Successfully exported ${successCount} VO files`, { variant: 'success' });
        } else {
          enqueueSnackbar(`Exported ${successCount}/${totalCount} VO files`, { variant: 'warning' });
        }
    } else if (outroSkipped) {
        enqueueSnackbar('Outro had invalid timestamps and was skipped. Updating sheet with defaults.', { variant: 'info' });
    }

    // --- Start: Logic for Social Clips (if Vertical is selected) ---
    const isVerticalSelected = outputFormat.includes('vertical');
    console.log('[OutputManager Export] Vertical format selected:', isVerticalSelected);

    if (isVerticalSelected) {
      console.log('[OutputManager Export] Proceeding with Vertical clip generation steps.');
      
      try {
        // First, check for Vertical URL column in the sheet
        const sheetData = await sheetsService.getData(renderSheetId, 'Sheet1!1:1');
        const sheetHeaders = sheetData.values?.[0] || [];
        const verticalUrlColIndex = sheetHeaders.findIndex((h: string) => h?.toLowerCase() === 'vertical url');
        let existingVerticalUrls: {[key: string]: string} = {};
        
        if (verticalUrlColIndex !== -1) {
          console.log('[OutputManager Export - Social] Found Vertical URL column at index:', verticalUrlColIndex);
          
          // Get existing vertical URLs for all positions
          const verticalCol = String.fromCharCode(65 + verticalUrlColIndex);
          const ranges = [];
          for (let row = 2; row <= 11; row++) {
            ranges.push(`Sheet1!${verticalCol}${row}`);
          }
          
          const batchData = await sheetsService.batchGetValues(renderSheetId, ranges);
          for (let row = 2; row <= 11; row++) {
            const key = String(12 - row); // Convert row 2 to position 10, row 11 to position 1
            const range = `Sheet1!${verticalCol}${row}`;
            const verticalUrl = batchData[range]?.values?.[0]?.[0] || '';
            
            // Add specific debug info for position 1 (row 11)
            if (row === 11) {
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - Row: ${row}, Range: ${range}`);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - Raw data:`, batchData[range]);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - URL value: "${verticalUrl}"`);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - URL length: ${verticalUrl.length}`);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - URL trimmed: "${verticalUrl.trim()}"`);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - Will be skipped: ${Boolean(verticalUrl.trim())}`);
            }
            
            // Use trimmed value and check for non-empty string
            if (verticalUrl.trim()) {
              existingVerticalUrls[key] = verticalUrl.trim();
            }
          }
          
          console.log('[OutputManager Export - Social] Found existing vertical URLs:', existingVerticalUrls);
        } else {
          console.log('[OutputManager Export - Social] No Vertical URL column found in sheet');
        }
        
        // Get the spreadsheet name to use as sheet name
        const sheetName = formattedSheetDate;
        
        // Build clip instructions for the API using voiceOver data and the S3 data
        const clipInstructions = [];
        const voS3Bucket = 'c1-scott-scratchdisk';
        const voS3BasePrefix = `Billboard/VO/${formattedSheetDate}/`;

        for (let i = 10; i >= 1; i--) {
            const key = String(i);
            const position = chartPositions[key] || key;
            const videoS3Url = s3Urls[key];
            const videoInPointStr = videoInPoints[key];
            const videoDurationStr = videoDurations[key];
            const title = songTitles[key];
            const artist = songArtists[key];
            const voInPointStr = voInPoints[key];
            const voOutPointStr = voOutPoints[key];
            
            // Skip if this position already has a vertical URL
            if (existingVerticalUrls[key]) {
              console.log(`[OutputManager Export - Social] Skipping position ${key} - "${title}" by "${artist}" already has a Vertical URL: ${existingVerticalUrls[key]}`);
              continue;
            }
            
            // Special debug for position 1
            if (key === '1') {
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - Processing check:`);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - existingVerticalUrls keys:`, Object.keys(existingVerticalUrls));
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - existingVerticalUrls['1']:`, existingVerticalUrls['1']);
              console.log(`[OutputManager Export - Social] POSITION 1 DEBUG - Will be processed: ${!existingVerticalUrls[key]}`);
            }
            
            const predictedVoS3Key = `${voS3BasePrefix}${key}.wav`;
            const predictedVoS3Url = `s3://${voS3Bucket}/${predictedVoS3Key}`;

            if (!videoS3Url) {
                console.warn(`[OutputManager Export - Social] Skipping position ${key}: Missing Video S3 URL.`);
                continue;
            }
            if (!title || !artist) {
                console.warn(`[OutputManager Export - Social] Skipping position ${key}: Missing Song Title or Artist.`);
                continue;
            }

            // Add validation to check if song and artist are not identical
            if (title === artist) {
                console.warn(`[OutputManager Export - Social] Warning for position ${key}: Song title "${title}" is identical to artist "${artist}". This may indicate data issues.`);
            }
            
            // Extract song name from S3 URL for debugging
            const songFromUrl = videoS3Url.split('/').pop()?.split('.')[0] || '';
            console.log(`[OutputManager Export - Social] Position ${key} data debugging:`);
            console.log(`  - Spreadsheet Song: "${title}"`);
            console.log(`  - Spreadsheet Artist: "${artist}"`);
            console.log(`  - S3 URL Filename: "${videoS3Url.split('/').pop()}"`);
            console.log(`  - Expected format: "<Artist> - <Song>.mp4"`);
            
            // Log the data being used for this position
            console.log(`[OutputManager Export - Social] Position ${key} data: Title="${title}", Artist="${artist}", S3 URL=${videoS3Url.substring(0, 80)}...`);

            const videoInPoint = parseFloat(videoInPointStr || '0');
            const videoDuration = parseFloat(videoDurationStr || '0');
            const voInPoint = parseFloat(voInPointStr || '0');
            const voOutPoint = parseFloat(voOutPointStr || '0');

            if (isNaN(videoInPoint)) {
                console.warn(`[OutputManager Export - Social] Skipping position ${key}: Invalid Video IN Point (${videoInPointStr}).`);
                continue;
            }
            if (isNaN(videoDuration) || videoDuration <= 0) {
                console.warn(`[OutputManager Export - Social] Skipping position ${key}: Invalid Video Duration (${videoDurationStr}).`);
                continue;
            }
            
            const finalVoInPoint = isNaN(voInPoint) ? null : voInPoint;
            const finalVoOutPoint = isNaN(voOutPoint) ? null : voOutPoint;

            clipInstructions.push({
                s3Url: videoS3Url,
                position: parseInt(position, 10),
                song: title,
                artist: artist,
                voIn: voInPointStr || '0',
                voOut: voOutPointStr || '0',
                inPoint: videoInPointStr || '0',
                duration: videoDurationStr || '0'
            });
        }
        
        if (clipInstructions.length === 0) {
          console.warn('[OutputManager Export - Social] No valid clip instructions could be generated for vertical format.');
          console.log('[OutputManager Export - Social] This could be because:');
          console.log('[OutputManager Export - Social] 1. All positions already have vertical URLs');
          console.log('[OutputManager Export - Social] 2. Missing required data (video URLs, titles, artists)');
          console.log('[OutputManager Export - Social] 3. Invalid data (NaN values for timing)');
          console.log(`[OutputManager Export - Social] Existing vertical URLs count: ${Object.keys(existingVerticalUrls).length}`);
          console.log(`[OutputManager Export - Social] Total positions checked: 10`);
          
          // When no clips need to be generated, we should still proceed to final assembly
          // Don't set sieveJobWasInitiated = true since no job was started
          console.log('[OutputManager Export - Social] No vertical clips needed. Will proceed to final assembly.');
          // enqueueSnackbar('Could not generate instructions for vertical clips. Check sheet data.', { variant: 'warning', autoHideDuration: 8000 }); REMOVED THIS LINE
        } else {
          console.log(`[OutputManager Export] Prepared ${clipInstructions.length} clip instructions`);
          
          const response = await fetch(`${API_BASE_URL}/api/generate-social-clips`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sheetName,
              clipInstructions,
              weeklySheetId: propSpreadsheetId
            })
          });
          
          if (response.status !== 202) {
            const responseData = await response.json();
            throw new Error(responseData.error || 'Failed to start social clip generation');
          }
          
          const responseData = await response.json();
          const jobId = responseData.jobId;
          
          console.log(`[OutputManager Export] Social clip generation API call made. Job ID: ${jobId}. Flag sieveJobWasInitiated will be set to true.`);
          enqueueSnackbar(`Vertical clip generation initiated (Job ID: ${jobId}).`, { variant: 'info' });
          
          setSocialClipJobId(jobId);
          setIsPollingSocialClips(true);
          setSocialClipJobStatus('pending');
          setSocialClipJobProgress(0);
          setSocialClipErrors([]);
          sieveJobWasInitiated = true; // SET THE FLAG HERE
        }
      } catch (err) {
        const actualErrorMessage = err instanceof Error ? err.message : String(err);
        console.error('[OutputManager Export - Social] Error starting social clip generation:', actualErrorMessage);
        if (actualErrorMessage.toLowerCase().includes('failed to download')) {
            console.warn(`[OutputManager Export - Social] Suppressed snackbar for error (includes 'failed to download'): ${actualErrorMessage}`);
        } else {
            console.log(`[OutputManager Export - Social] Displaying snackbar for social clip error: ${actualErrorMessage}`);
            enqueueSnackbar(`Vertical clip generation error: ${actualErrorMessage}`, { variant: 'error' });
        }
      }
    }

    if (outroSkipped) {
      console.log('[OutputManager] Outro was skipped, calling updateSheetWithDefaultOutro.');
      await updateSheetWithDefaultOutro(renderSheetId);
    }

    if (error) {
      console.error('[OutputManager Export] Error before final assembly decision:', error);
      return; 
    }

    // ADD THIS LOG:
    console.log(`[OutputManager Export Decision Point] sieveJobWasInitiated: ${sieveJobWasInitiated}, socialClipJobId (state): ${socialClipJobId}, outputFormat: ${outputFormat.join(',')}`);

    if (sieveJobWasInitiated) {
        console.log('[OutputManager Export] Sieve job was initiated. Assembly will be triggered by pollSocialClipStatus upon completion.');
    } else {
        console.log('[OutputManager Export] No Sieve job was initiated. Proceeding to final video assembly now.');
        
        // Check if vertical was selected but no clips were generated
        const verticalWasSelected = outputFormat.includes('vertical');
        const horizontalWasSelected = outputFormat.includes('horizontal');
        
        if (verticalWasSelected && horizontalWasSelected) {
          console.log('[OutputManager Export] Both formats selected. No vertical clips needed (all positions already have URLs). Processing both formats.');
          await triggerFinalVideoAssembly();
        } else if (horizontalWasSelected && !verticalWasSelected) {
          console.log('[OutputManager Export] Only horizontal selected, processing immediately.');
          await triggerFinalVideoAssembly();
        } else if (verticalWasSelected && !horizontalWasSelected) {
          console.log('[OutputManager Export] Only vertical selected but no clips needed. Processing vertical format.');
          await triggerFinalVideoAssembly();
        } else {
          console.log('[OutputManager Export] No formats selected or unknown state. Processing default.');
          await triggerFinalVideoAssembly();
        }
    }

    } catch (err) {
      console.error('[OutputManager Export] Error:', err);
      setError(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
      enqueueSnackbar(`Export failed: ${err instanceof Error ? err.message : String(err)}`, { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };
  
  // Helper function to get sheet name from a spreadsheet ID
  const getSheetName = async (spreadsheetId: string): Promise<string> => {
    try {
      // You might already have a function to get the spreadsheet name
      // This is a placeholder - replace with your actual implementation
      const spreadsheetInfo = await sheetsService.getSpreadsheet(spreadsheetId);
      return spreadsheetInfo?.properties?.title || `sheet-${Date.now()}`;
    } catch (error) {
      console.error('[OutputManager] Error getting sheet name:', error);
      // Fallback to a timestamp-based name
      return `sheet-${Date.now()}`;
    }
  };
  
  const updateSheetWithDefaultOutro = async (sheetIdToUpdate: string) => {
      if (!sheetIdToUpdate) {
        console.error('[OutputManager] Cannot update sheet: No spreadsheet ID provided for default outro.');
        return;
      }
      console.log(`[OutputManager] Updating sheet ${sheetIdToUpdate} with default Outro values.`);
      try {
        const headersArray = await sheetsService.getHeaders(sheetIdToUpdate, 'Sheet1!1:1');
        if (!headersArray || headersArray.length === 0) {
          throw new Error('Could not fetch headers to update default outro.');
        }

        const findHeaderIndex = (headerName: string) => 
          headersArray.findIndex(h => h.toLowerCase().includes(headerName.toLowerCase()));

        const voUrlColIndex = findHeaderIndex('vo url');
        const voInColIndex = findHeaderIndex('vo in');
        const voOutColIndex = findHeaderIndex('vo out');

        const voUrlCol = voUrlColIndex !== -1 ? String.fromCharCode(65 + voUrlColIndex) : null;
        const voInCol = voInColIndex !== -1 ? String.fromCharCode(65 + voInColIndex) : null;
        const voOutCol = voOutColIndex !== -1 ? String.fromCharCode(65 + voOutColIndex) : null;

        if (!voUrlCol || !voInCol || !voOutCol) {
          console.error('[OutputManager] Could not determine one or more column letters (URL, In, Out) for default Outro update based on detected headers.',
            { headersArray, voUrlColIndex, voInColIndex, voOutColIndex });
          setError('Failed to determine columns for Outro update. Check sheet headers for VO URL, VO In, VO Out.');
          setShowErrorAlert(true);
          setAlertMessage('Failed to determine columns for Outro update. Check sheet headers for VO URL, VO In, VO Out.');
          return;
        }

        const updates = [
          { range: `Sheet1!${voUrlCol}13`, values: [['default-outro.wav']] },
          { range: `Sheet1!${voInCol}13`, values: [[0]] },
          { range: `Sheet1!${voOutCol}13`, values: [[7]] },
        ];

        await sheetsService.batchUpdateValues(sheetIdToUpdate, updates);
        console.log('[OutputManager] Successfully updated render sheet with default Outro values.');
        setAlertMessage('Default Outro values (URL, In/Out) have been updated on the render sheet.');
        setShowSuccessAlert(true);
      } catch (err) {
        console.error('[OutputManager] Error updating render sheet with default Outro:', err);
        setError(`Failed to update Outro on sheet: ${err instanceof Error ? err.message : String(err)}`);
        setShowErrorAlert(true);
        setAlertMessage(`Error updating Outro on sheet: ${err instanceof Error ? err.message : String(err)}`);
      }
  };

  const handleValidationDialogClose = () => {
    setValidationDialogOpen(false);
    if (renderSheetId) {
      console.log(`Navigating to /intro-outro/${renderSheetId}`);
      navigate(`/intro-outro/${renderSheetId}`);
    } else {
      console.error("Cannot navigate to Intro/Outro: renderSheetId is null");
      enqueueSnackbar("Cannot navigate: Render Sheet ID is missing.", { variant: "error" });
    }
  };

  const handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormats: string[],
  ) => {
    if (newFormats.length > 0) {
      setOutputFormat(newFormats);
    } else {
      if (outputFormat.length === 1) {
        if (outputFormat[0] === 'horizontal') {
          setOutputFormat(['vertical']);
        } 
        else if (outputFormat[0] === 'vertical') {
          setOutputFormat(['horizontal']);
        }
      } 
    }
  };

  // Function to poll json2video for rendering status
  const pollMovieRenderStatus = useCallback(() => {
    console.log('[OutputManager Movie Polling] pollMovieRenderStatus invoked');
    
    let currentTimerId: NodeJS.Timeout | null = null;

    const checkStatus = async () => {
      if (!isPollingMovieStatusRef.current) {
        console.log('[OutputManager Movie Polling] checkStatus: Polling REF is false. Stopping.');
        if (currentTimerId) clearTimeout(currentTimerId);
        return;
      }

      const currentProjects = movieProjectsRef.current; // Use ref to get latest projects
      
      if (currentProjects.length === 0) {
        console.log('[OutputManager Movie Polling] checkStatus: No projects to poll. Setting isPollingMovieStatus to false.');
        setIsPollingMovieStatus(false); // No projects, so ensure polling stops
        if (currentTimerId) clearTimeout(currentTimerId);
        return;
      }
      
      const projectsToPoll = currentProjects.filter(p => p.status !== 'done' && p.status !== 'error' && p.status !== 'finalized_s3'); // Added 'finalized_s3'

      if (projectsToPoll.length === 0) {
        console.log('[OutputManager Movie Polling] checkStatus: All projects are in terminal state. Setting isPollingMovieStatus to false.');
        setIsPollingMovieStatus(false); // All done, so ensure polling stops
        if (currentTimerId) clearTimeout(currentTimerId);
        return;
      }
      
      console.log(`[OutputManager Movie Polling] checkStatus: Polling ${projectsToPoll.length} projects.`);

      const updatedProjectsPromises = currentProjects.map(async (project) => {
        if (project.status === 'done' || project.status === 'error' || project.status === 'finalized_s3') {
          return project;
        }
        
        try {
          const response = await fetch(`https://api.json2video.com/v2/movies?project=${project.id}`, {
            headers: {
              'x-api-key': 'r6eXWIhUKTcDNNgqG2RdiSY8PtpFuEmDgb6fYYlu' // Consider moving to env var
            }
          });
          
          if (!response.ok) {
            console.error(`[OutputManager Movie Polling] Server error for project ${project.id}: ${response.status}`);
            // Potentially update project status to an API error state
            return {...project, status: 'api_error'}; 
          }
          
          const responseData = await response.json();
          
          if (responseData.success && responseData.movie) {
            const newStatus = responseData.movie.status;
            const prevStatus = project.status;
            
            if (newStatus !== prevStatus) {
              console.log(`[OutputManager Movie Polling] Project ${project.id} old status: ${prevStatus}, new status: ${newStatus}`);
            }
            
            const updatedProjectData = {
              ...project,
              status: newStatus,
              url: responseData.movie.url || project.url // Keep old URL if new one is missing
            };
            
            if (newStatus === 'done' && prevStatus !== 'done') {
              console.log(`[OutputManager Movie Polling] Project ${project.id} (${project.format}) completed by json2video. URL: ${responseData.movie.url}`);
              enqueueSnackbar(`${project.format} video rendering complete by json2video. Finalizing to S3...`, { variant: 'info' });

              // Call backend to download from json2video URL and upload to S3
              try {
                const finalizeResponse = await fetch(`${API_BASE_URL}/api/finalize-video`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    videoUrl: responseData.movie.url,
                    s3FolderName: 'Billboard/Outputs',
                    baseFileName: baseFileNameForExport || project.id, // Use baseFileName or project.id as fallback
                    format: project.format, // "Horizontal" or "Vertical"
                    sheetDate: sheetDate 
                  })
                });

                if (!finalizeResponse.ok) {
                  const errorBody = await finalizeResponse.text();
                  throw new Error(`Failed to finalize video to S3: ${finalizeResponse.status} ${errorBody}`);
                }
                const finalizeResult = await finalizeResponse.json();
                if (finalizeResult.success && finalizeResult.s3Url) {
                  console.log(`[OutputManager Movie Polling] Project ${project.id} (${project.format}) finalized to S3. New URL: ${finalizeResult.s3Url}`);
                  enqueueSnackbar(`${project.format} video successfully saved to S3!`, { variant: 'success' });
                  setRecentOutputs(finalizeResult.manifest || []); // Update recent outputs list
                  return { ...updatedProjectData, status: 'finalized_s3', url: finalizeResult.s3Url };
                } else {
                  throw new Error(finalizeResult.error || 'S3 finalization failed with no specific error.');
                }
              } catch (finalizeError: any) {
                console.error(`[OutputManager Movie Polling] Error finalizing project ${project.id} (${project.format}) to S3:`, finalizeError);
                enqueueSnackbar(`Error saving ${project.format} video to S3: ${finalizeError.message}`, { variant: 'error' });
                return { ...updatedProjectData, status: 'error' }; // Keep json2video URL, mark as error
              }
            } else if (newStatus === 'error' && prevStatus !== 'error') {
              const movieErrorMessage = responseData.movie.message || 'Unknown error from json2video';
              console.error(`[OutputManager Movie Polling] Project ${project.id} (${project.format}) FAILED (json2video). Message: ${movieErrorMessage}`, responseData.movie);
              enqueueSnackbar(`${project.format} video rendering failed (json2video): ${movieErrorMessage}`, { variant: 'error' });
            } else if (newStatus === 'rendering' && prevStatus !== 'rendering') {
              console.log(`[OutputManager Movie Polling] Project ${project.id} now rendering.`);
            }
            return updatedProjectData;
          }
          return project; // Return original project if responseData or movie is not as expected
        } catch (error) {
          console.error(`[OutputManager Movie Polling] Error checking project ${project.id}:`, error);
          // Potentially update project status to a network error state
          return {...project, status: 'network_error'}; 
        }
      });
      
      const newUpdatedProjects = await Promise.all(updatedProjectsPromises);
      setMovieProjects(newUpdatedProjects); 

      const anyProjectStillActiveAfterUpdate = newUpdatedProjects.some(p => p.status !== 'finalized_s3' && p.status !== 'error' && p.status !== 'api_error' && p.status !== 'network_error');
      
      if (isPollingMovieStatusRef.current && anyProjectStillActiveAfterUpdate) {
        currentTimerId = setTimeout(checkStatus, 10000); // Poll every 10 seconds
      } else {
        console.log('[OutputManager Movie Polling] All projects terminal or polling stopped. Halting movie polling.');
        setIsPollingMovieStatus(false); 
        if (currentTimerId) clearTimeout(currentTimerId);
      }
    };

    // Initial call to start polling if active projects exist
    if (isPollingMovieStatusRef.current && movieProjectsRef.current.some(p => p.status !== 'finalized_s3' && p.status !== 'error')) {
       console.log('[OutputManager Movie Polling] Initial call to checkStatus from pollMovieRenderStatus.');
       currentTimerId = setTimeout(checkStatus, 1000); // Start fairly quickly
    } else {
      console.log('[OutputManager Movie Polling] No active projects to poll initially or polling ref is false.');
    }
    
    return () => {
      if (currentTimerId) {
        console.log('[OutputManager Movie Polling] Cleanup: Clearing movie polling timer.');
        clearTimeout(currentTimerId);
      }
    };
  }, [enqueueSnackbar, sheetDate, baseFileNameForExport]); // Added sheetDate and baseFileNameForExport dependencies

  useEffect(() => {
    let pollCleanupFunction: (() => void) | undefined;

    if (isPollingMovieStatus) {
      console.log('[OutputManager useEffect Polling Trigger] isPollingMovieStatus is true. Calling pollMovieRenderStatus to start/ensure polling.');
      pollCleanupFunction = pollMovieRenderStatus(); // pollMovieRenderStatus now returns its own timer cleanup
    } else {
      // If isPollingMovieStatus becomes false, we rely on the cleanup from the previous run (if any)
      // and the checkStatus internal logic using isPollingMovieStatusRef to stop timeouts.
      console.log('[OutputManager useEffect Polling Trigger] isPollingMovieStatus is false. Polling should stop/be stopped.');
    }
    
    return () => {
        if (pollCleanupFunction) {
            console.log('[OutputManager useEffect Polling Trigger] useEffect cleanup: Calling cleanup from pollMovieRenderStatus.');
            pollCleanupFunction();
        }
    };
  }, [isPollingMovieStatus, pollMovieRenderStatus]); // pollMovieRenderStatus is stable

  // New useEffect to get parentSpreadsheetId from URL query
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const parentIdFromQuery = queryParams.get('parentSpreadsheetId');
    if (parentIdFromQuery) {
      setParentSpreadsheetId(parentIdFromQuery);
      console.log(`[OutputManager] Parent Spreadsheet ID from query: ${parentIdFromQuery}`);
    } else {
      console.warn('[OutputManager] parentSpreadsheetId not found in query params. VO/Audio settings might not load correctly.');
    }
  }, [location.search]);

  useEffect(() => {
    setActiveSpreadsheetId(propSpreadsheetId);
  }, [propSpreadsheetId]);

  // Effect to fetch recent outputs on component mount
  useEffect(() => {
    const fetchRecentOutputs = async () => {
      try {
        console.log('[OutputManager] Fetching recent outputs...');
        const response = await fetch(`${API_BASE_URL}/api/get-recent-outputs`);
        if (!response.ok) {
          throw new Error(`Failed to fetch recent outputs: ${response.status}`);
        }
        const data = await response.json();
        console.log('[OutputManager] Received recent outputs:', data); // <--- ADD THIS LOG
        setRecentOutputs(data || []);
        console.log('[OutputManager] Recent outputs loaded:', data);
      } catch (fetchError: any) {
        console.error('[OutputManager] Error fetching recent outputs:', fetchError);
        // Optionally set an error state here or show a snackbar
        // enqueueSnackbar(`Could not load recent renders: ${fetchError.message}`, { variant: 'warning' });
      }
    };

    fetchRecentOutputs();
    // Also sync S3 outputs with the manifest on mount
    const syncS3 = async () => {
      try {
        console.log('[OutputManager] Calling /api/sync-s3-outputs...');
        const response = await fetch(`${API_BASE_URL}/api/sync-s3-outputs`, { method: 'POST' });
        const data = await response.json();
        if (response.ok && data.success) {
          console.log('[OutputManager] S3 sync successful:', data.message);
        } else {
          console.error('[OutputManager] S3 sync failed:', data.error || 'Unknown error');
        }
      } catch (err) {
        console.error('[OutputManager] Error calling /api/sync-s3-outputs:', err);
      }
    };
    syncS3();
  }, []); // Empty dependency array means this runs once on mount

  // Effect to fetch sheet name and set renderSheetId, sheetDate, and baseFileNameForExport
  useEffect(() => {
    const fetchSheetDetails = async () => {
      if (propSpreadsheetId) {
        // setRenderSheetId(propSpreadsheetId); // <--- REMOVED THIS LINE
        try {
          const name = await getSheetName(propSpreadsheetId);
          if (name) {
            // Extract date from sheet name (assuming format like "YYYY-MM-DD Showdown")
            const dateMatch = name.match(/(\d{4}-\d{2}-\d{2})/); // Matches YYYY-MM-DD
            const simpleDateMatch = name.match(/(\d{1,2}-\d{1,2}-\d{2,4})/); // Matches M-D-YY or MM-DD-YYYY etc.

            let extractedDate = null;
            if (dateMatch) {
              extractedDate = dateMatch[1];
            } else if (simpleDateMatch) {
              extractedDate = simpleDateMatch[1];
            }
            setSheetDate(extractedDate);
            // Create a base filename from the sheet name (e.g., "YYYY-MM-DD_Showdown")
            setBaseFileNameForExport(name.replace(/[^a-zA-Z0-9_.-]/g, '_').replace(/__/g, '_'));
            console.log(`[OutputManager] Sheet Name: ${name}, Extracted Date: ${extractedDate}, Base Filename: ${name.replace(/[^a-zA-Z0-9_.-]/g, '_').replace(/__/g, '_')}`);
          }
        } catch (err) {
          console.error("[OutputManager] Error fetching sheet name:", err); // Corrected line
          setError("Failed to fetch sheet name."); // Corrected line
        }
      }
      setLoading(false);
    };
    fetchSheetDetails();
  }, [propSpreadsheetId]); // Re-run if propSpreadsheetId changes

  const handleRecentOutputClick = (url: string) => {
    console.log('[OutputManager] Recent output clicked, URL:', url);
    setSelectedRecentVideoUrl(url);
  };

  // Function to format timestamp
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (!propSpreadsheetId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 4, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>No Chart Date Selected</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>Please select a date on the Charts page first.</Typography>
        <Button 
          onClick={() => navigate('/')} 
          variant="contained"
          sx={{ 
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Go to Charts
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isGenerating = isExporting || isPollingSocialClips;

  // Helper function for downloads
  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const completedVideos = movieProjects.filter(p => p.status === 'finalized_s3' && p.url);
  const horizontalVideo = completedVideos.find(p => p.format.toLowerCase() === 'horizontal');
  const verticalVideo = completedVideos.find(p => p.format.toLowerCase() === 'vertical');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={outputFormat}
            onChange={handleFormatChange}
            aria-label="output format"
            size="medium"
            sx={{
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
              height: '46px', // Explicit height for the group
            }}
          >
            <ToggleButton value="horizontal" aria-label="horizontal format" sx={{ height: '100%' }}>
              Horizontal
            </ToggleButton>
            <ToggleButton value="vertical" aria-label="vertical format" sx={{ height: '100%' }}>
              Vertical
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={!isGenerating ? <AutoAwesomeIcon /> : undefined}
            onClick={isGenerating ? handleCancelGeneration : handleExport}
            disabled={ (loading || !!error || outputFormat.length === 0 || !isReadyForExport) && !isGenerating } // Added !isReadyForExport
            sx={{
              backgroundColor: isGenerating ? 'error.main' : '#00FF9A',
              color: isGenerating ? 'error.contrastText' : '#000000',
              '&:hover': {
                backgroundColor: isGenerating ? 'error.dark' : '#00e68a',
                color: isGenerating ? 'error.contrastText' :'#000000',
              },
              height: '46px',
            }}
          >
            {isGenerating ? 'CANCEL' : (isExporting ? 'Generating...' : 'GENERATE')}
          </Button>
        </Box>
        <Button
          onClick={() => navigate('/')}
          variant="outlined"
          size="medium"
          sx={{
            textTransform: 'none',
            height: '46px', // Explicit height for this button
          }}
        >
          CHARTS
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      
      {showSuccessAlert && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowSuccessAlert(false)}>
          {alertMessage}
        </Alert>
      )}
      
      {isPollingSocialClips && socialClipJobStatus && socialClipJobStatus !== 'completed' && socialClipJobStatus !== 'failed' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Vertical clip generation: {socialClipJobStatus}
          {socialClipJobProgress !== null && ` (${socialClipJobProgress}%)`}
        </Alert>
      )}
      {socialClipJobStatus === 'completed' && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSocialClipJobStatus(null)}>
          Vertical clips generated successfully.
        </Alert>
      )}
      {socialClipErrors && socialClipErrors.length > 0 && socialClipJobStatus === 'failed' && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => { setSocialClipErrors([]); setSocialClipJobStatus(null); } }>
          Vertical clip generation failed: {socialClipErrors.join('; ')}
        </Alert>
      )}
      
      {/* Movie Rendering Status - Updated Alerts */}
      {movieProjects.filter(p => p.status !== 'finalized_s3').length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Final Video Rendering Status:</Typography>
          {movieProjects
            .filter(project => project.status !== 'finalized_s3') // Exclude already finalized videos
            .map((project) => {
              let severity: 'info' | 'success' | 'warning' | 'error' = 'info';
              let message = 'Processing...';

              if (project.status === 'done') {
                severity = 'info';
                message = 'Finalizing (saving to S3)...';
                if (project.url) {
                  message += ' '; // Add a space before the link
                }
              } else if (project.status === 'error') {
                severity = 'error';
                message = 'Failed to render. Check logs.';
              } else if (project.status === 'api_error' || project.status === 'network_error') {
                severity = 'warning';
                message = 'Communication error with render service.';
              } else if (project.status === 'processing' || project.status === 'rendering') {
                severity = 'info';
                message = 'Processing...';
              }
              // For any other statuses, it will default to 'info' and 'Processing...'

              return (
                <Alert
                  key={`${project.id}-${project.status}`}
                  severity={severity}
                  sx={{ mb: 1 }}
                >
                  {project.format} video: {message}
                  {project.status === 'done' && project.url && (
                    <Link href={project.url} target="_blank" rel="noopener noreferrer" sx={{ ml: 0.5 }}>
                      (View source render)
                    </Link>
                  )}
                </Alert>
              );
            })}
        </Box>
      )}

      {/* --- START: NEW VIDEO PLAYER SECTION --- */}
      {completedVideos.length > 0 && (
        <Box sx={{ mt: 3, mb: 3, border: '1px solid #ccc', borderRadius: '4px', p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {completedVideos.length > 1 ? 'Rendered Videos' : 'Rendered Video'}
          </Typography>
          {completedVideos.length > 1 && (horizontalVideo && verticalVideo) ? (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activePlayerTab} onChange={(event, newValue) => setActivePlayerTab(newValue)} aria-label="video player tabs">
                  {horizontalVideo && <Tab label="Horizontal (16:9)" />}
                  {verticalVideo && <Tab label="Vertical (9:16)" />}
                </Tabs>
              </Box>
              {horizontalVideo && activePlayerTab === 0 && (
                <Box sx={{ mt: 2 }}>
                  <video src={horizontalVideo.url} controls width="100%" style={{ aspectRatio: '16/9', maxHeight: '60vh' }} />
                </Box>
              )}
              {verticalVideo && activePlayerTab === (horizontalVideo ? 1 : 0) && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <video src={verticalVideo.url} controls style={{ height: '60vh', aspectRatio: '9/16' }} />
                </Box>
              )}
            </Box>
          ) : completedVideos.length === 1 && completedVideos[0].url ? (
            <Box sx={{ mt: 2 }}>
              <video 
                src={completedVideos[0].url} 
                controls 
                width={completedVideos[0].format.toLowerCase() === 'vertical' ? undefined : "100%"}
                style={{ 
                  aspectRatio: completedVideos[0].format.toLowerCase() === 'vertical' ? '9/16' : '16/9', 
                  height: completedVideos[0].format.toLowerCase() === 'vertical' ? '60vh' : undefined,
                  maxHeight: completedVideos[0].format.toLowerCase() === 'horizontal' ? '60vh' : undefined,
                  display: completedVideos[0].format.toLowerCase() === 'vertical' ? 'block' : undefined,
                  margin: completedVideos[0].format.toLowerCase() === 'vertical' ? '0 auto' : undefined,
                }} 
              />
            </Box>
          ) : null}

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
            {horizontalVideo && (
              <Button 
                variant="outlined" 
                onClick={() => handleDownload(horizontalVideo.url!, `${sheetDate || 'video'}_horizontal.mp4`)}
              >
                Download 16:9
              </Button>
            )}
            {verticalVideo && (
              <Button 
                variant="outlined" 
                onClick={() => handleDownload(verticalVideo.url!, `${sheetDate || 'video'}_vertical.mp4`)}
              >
                Download 9:16
              </Button>
            )}
            {horizontalVideo && verticalVideo && (
              <Button
                variant="contained"
                onClick={() => {
                  console.log("[Download Both] Attempting Horizontal Download.");
                  console.log("[Download Both] Horizontal URL:", horizontalVideo.url);
                  console.log("[Download Both] Horizontal Filename:", `${sheetDate || 'video'}_horizontal.mp4`);
                  handleDownload(horizontalVideo.url!, `${sheetDate || 'video'}_horizontal.mp4`);
                  // Add a small delay before starting the second download
                  setTimeout(() => {
                    console.log("[Download Both] Attempting Vertical Download (after delay).");
                    console.log("[Download Both] Vertical URL:", verticalVideo.url);
                    console.log("[Download Both] Vertical Filename:", `${sheetDate || 'video'}_vertical.mp4`);
                    handleDownload(verticalVideo.url!, `${sheetDate || 'video'}_vertical.mp4`);
                  }, 500); // Increased delay to 500ms
                }}
              >
                Download Both
              </Button>
            )}
          </Box>
        </Box>
      )}
      {/* --- END: NEW VIDEO PLAYER SECTION --- */}
    
      {/* Audio processing UI */}
      <Dialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        aria-labelledby="validation-dialog-title"
        aria-describedby="validation-dialog-description"
      >
        <DialogTitle id="validation-dialog-title">Missing Information</DialogTitle>
        <DialogContent>
          <DialogContentText id="validation-dialog-description" sx={{ mb: 2 }}>
            The following required information is missing from the Intro/Outro section:
          </DialogContentText>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {validationErrors.map((errorMsg, index) => (
              <ListItem key={index} disableGutters sx={{ pl: 2 }}>
                <ListItemText primary={`• ${errorMsg}`} />
              </ListItem>
            ))}
          </List>
          <DialogContentText sx={{ mt: 2 }}>
            Click OK to go to the Intro/Outro page and add the missing values.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleValidationDialogClose} autoFocus variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recent Renders Section - Always Visible */}
      {recentOutputs && ( // Ensure recentOutputs is not null before trying to access its length or map over it
        <Box sx={{ width: '100%', maxWidth: 700, marginTop: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', fontWeight: 'medium' }}>
            Recent Renders
          </Typography>
          {selectedRecentVideoUrl && (
            <Box sx={{ marginBottom: 2, border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
              <video key={selectedRecentVideoUrl} width="100%" controls autoPlay muted>
                <source src={selectedRecentVideoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Box>
          )}
          <Box sx={{ border: '1px solid #ddd', borderRadius: '4px' }}> {/* Removed maxHeight and overflow */}
            {recentOutputs.length > 0 ? (
              <List sx={{ paddingTop: 0, paddingBottom: 0 }}> {/* Removed maxHeight and overflow */}
                {recentOutputs.map((output, index) => (
                  <ListItem 
                    key={output.s3Url || index} 
                    divider 
                    sx={{ 
                      paddingTop: 0, // Remove padding from ListItem itself
                      paddingBottom: 0,
                      // backgroundColor will be handled by ListItemButton or kept on ListItem if preferred
                    }}
                    disablePadding // Add disablePadding since ListItemButton will handle padding
                  >
                    <ListItemButton
                      onClick={() => {
                        console.log('[OutputManager] ListItemButton onClick triggered for URL:', output.s3Url);
                        handleRecentOutputClick(output.s3Url);
                      }}
                      sx={{
                        backgroundColor: selectedRecentVideoUrl === output.s3Url ? 'lightblue' : (index % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'transparent'),
                        paddingTop: '8px', // Apply padding to ListItemButton
                        paddingBottom: '8px',
                        // cursor: 'pointer' is inherent to ListItemButton
                      }}
                    >
                      <IconButton 
                        edge="start" 
                        aria-label="download" 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent ListItem click when icon is clicked
                          console.log('[OutputManager] Download icon clicked for:', output.fileName);
                          handleDownload(output.s3Url, output.fileName);
                        }}
                        sx={{ marginRight: '12px' }} // Add some margin to the right of the icon
                      >
                        <DownloadIcon />
                      </IconButton>
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}> {/* Box to hold text items */}
                        <ListItemText
                          primary={`${output.sheetDate || 'N/A'} | ${output.format || 'N/A'}`}
                          secondary={output.timestamp ? formatTimestamp(output.timestamp) : 'No timestamp'}
                          primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium' }}
                          secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography sx={{ textAlign: 'center', padding: 2, color: 'text.secondary' }}>
                No recent renders available.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', width: '100%', paddingTop: '0px' }}>
        {/* Add your main content components here */}
      </Box>
    </Box>
  );
};